import React, { useState, useEffect, useMemo } from 'react';
import { useScenario, useEntityFilter, useTagFilter, useCurrencyFilter, useSubtagFilter, useCalculationType } from '../components/layout/Layout';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, LinearProgress, Autocomplete, IconButton, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox, FormGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EnhancedDataTable from '../components/EnhancedDataTable';
import api from '../services/api';
import { adjustmentTypeToRouteMap } from '../utils/adjustmentTypeUtils';
import '../styles/EntityInputs.css';

const TAX_CALCULATIONS_COLUMNS = [
{ field: 'EntityCode', headerName: 'Entity', minWidth: 120 },
{ field: 'EntityName', headerName: 'Entity Name', minWidth: 180, flex: 1 },
{ field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
{ field: 'AdjustmentName', headerName: 'Adjustment Name', width: 600 },
{ field: 'Tag', headerName: 'Tag', minWidth: 120 },
{ field: 'LocalCurrencyInput', headerName: 'Local Currency Input', minWidth: 160, type: 'number' },
{ field: 'USDInput', headerName: 'USD Input', minWidth: 120, type: 'number' },
{ field: 'TaxEffectedLocalAmount', headerName: 'Tax-Effected Local Amount', minWidth: 180, type: 'number' },
{ field: 'TaxEffectedUSDAmount', headerName: 'Tax-Effected USD Amount', minWidth: 180, type: 'number' },
{ field: 'AdjustmentSource', headerName: 'Adjustment Source', minWidth: 140 }];


// Deferred rollforward table headers
const DEFERRED_ROLLFORWARD_HEADERS = [
'GAAP TO STAT TEMPORARY DIFFERENCES',
'STAT TO TAX TEMPORARY DIFFERENCES',
'TAXABLE INCOME TEMP ADJUSTMENT',
'NET OPERATING LOSS',
'VALUATION ALLOWANCE',
'TAX EFFECTED TEMPORARY DIFFERENCES',
'TAX EFFECTED VALUATION ALLOWANCE'];


// Activity types for deferred rollforward columns
const ACTIVITY_TYPES = [
'BeginningBalance',
'ReturnToProvision',
'DeferredTrueUp',
'PurchaseAccounting',
'CurrentActivityPL',
'CurrentActivityEQ',
'OCIAdjustment',
'DeferredExpAdjustment',
'BalanceSheetAdjustment',
'CustomDeferredExpAdjustment',
'CustomBalanceSheetAdjustment'];


// Tag and SubTag options - stored on frontend as temporary solution
const TAG_OPTIONS = [
'ORDINARY',
'DISCRETE',
'RTP',
'AUDIT',
'OCI',
'PPA',
'Deferred Only',
'Deferred Only PY',
'Balance Sheet Only',
'Balance Sheet Only PY',
'Valuation Allowance',
'Valuation Allowance PY',
'Uncertain Tax Position',
'Uncertain Tax Position PY'];


const SUBTAG_OPTIONS = [
'Import',
'Auto',
'Manual',
'Workpaper',
'Q1',
'Q2',
'Q3',
'Q4',
'2024',
'2023',
'2022'];


// Utility function to format numbers with parentheses for negatives
const formatNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(numValue);
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

// Utility function to format rounded numbers with parentheses for negatives
const formatRoundedNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(Math.round(numValue));
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

const defaultCalculation = {
  EntityCode: '',
  EntityName: '',
  AdjustmentCode: '',
  AdjustmentName: '',
  Tag: '',
  LocalCurrencyInput: '',
  USDInput: '',
  TaxEffectedLocalAmount: '',
  TaxEffectedUSDAmount: '',
  AdjustmentSource: ''
};

const TaxCalculations = () => {
  // Check library availability on component mount
  const checkLibraryAvailability = () => {





    if (window.XLSX) {

    }

    // Test if we can access the required methods
    try {
      if (window.XLSX && window.XLSX.utils) {




      }
    } catch (error) {
      console.error('Error checking XLSX methods:', error);
    }


  };

  // Export to Excel
  const exportToExcel = () => {



    try {
      // Check if libraries are loaded
      if (typeof window.XLSX === 'undefined' || typeof window.saveAs === 'undefined') {
        console.error('Libraries missing - XLSX:', typeof window.XLSX, 'saveAs:', typeof window.saveAs);
        setNotification({
          open: true,
          message: 'Excel export is not available. Please ensure XLSX and FileSaver are loaded.',
          severity: 'warning'
        });
        return;
      }



      // Get the data to export
      let dataToExport = selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

      if (dataToExport.length === 0) {

        setNotification({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      let excelData = [];

      if (calculationType === 'deferred-rollforward') {
        // Handle deferred rollforward export with activity type columns


        excelData = dataToExport.map((row) => {
          const excelRow = {};

          // Handle row type and basic info
          if (row.isSection) {
            excelRow['Type'] = 'SECTION HEADER';
            excelRow['Entity Code'] = '';
            excelRow['Adjustment Code'] = '';
            excelRow['Description'] = row.AdjustmentName;
          } else if (row.isSubFooter) {
            excelRow['Type'] = 'SECTION TOTAL';
            excelRow['Entity Code'] = '';
            excelRow['Adjustment Code'] = '';
            excelRow['Description'] = row.AdjustmentName;
          } else {
            excelRow['Type'] = 'ADJUSTMENT';
            excelRow['Entity Code'] = row.EntityCode || '';
            excelRow['Adjustment Code'] = row.AdjustmentCode || '';
            excelRow['Description'] = row.AdjustmentName || '';
          }

          // Add all activity type columns (pretax)
          ACTIVITY_TYPES.forEach((activityType) => {
            const columnName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (PRETAX)`;
            excelRow[columnName] = row[activityType] || 0;
          });

          // Add CTA Adjustment and Ending Balance
          excelRow['CTA ADJUSTMENT (PRETAX)'] = row['CTAAdjustment'] || 0;
          excelRow['ENDING BALANCE (PRETAX)'] = row['EndingBalance'] || 0;

          // Add all activity type columns (tax effected)
          ACTIVITY_TYPES.forEach((activityType) => {
            const columnName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (TAX EFFECTED)`;
            excelRow[columnName] = row[`${activityType}_TaxEffected`] || 0;
          });

          // Add additional tax effected columns
          excelRow['CTA ADJUSTMENT (TAX EFFECTED)'] = row['CTAAdjustment_TaxEffected'] || 0;
          excelRow['ENDING BALANCE (TAX EFFECTED)'] = row['EndingBalance_TaxEffected'] || 0;
          excelRow['TAX RATE CHANGE (TAX EFFECTED)'] = row['TaxRateChange_TaxEffected'] || 0;
          excelRow['DEFERRED EXPENSE'] = row['DeferredExpense'] || 0;
          excelRow['RETURN TO PROVISION'] = row['ReturnToProvision'] || 0;
          excelRow['DEFERRED TRUE UP'] = row['DeferredTrueUp'] || 0;
          excelRow['CURRENT ACTIVITY'] = row['CurrentActivity'] || 0;
          excelRow['WRITE OFF'] = row['WriteOff'] || 0;
          excelRow['VALUATION ALLOWANCE'] = row['ValuationAllowance'] || 0;

          return excelRow;
        });

      } else {
        // Handle current provision/effective tax rate export with entity columns


        // Get unique entities from the data to create entity columns
        const uniqueEntities = [...new Set(allRows.map((row) => row.EntityCode))].
        filter((code) => code).
        sort();



        excelData = dataToExport.map((row) => {
          const excelRow = {};

          // Handle row type and basic info
          if (row.isSection) {
            excelRow['Type'] = 'SECTION HEADER';
            excelRow['Adjustment Code'] = '';
            excelRow['Description'] = row.AdjustmentName;
          } else if (row.isSubFooter) {
            excelRow['Type'] = 'SECTION TOTAL';
            excelRow['Adjustment Code'] = '';
            excelRow['Description'] = row.AdjustmentName;
          } else if (row.isCalculatedTotal) {
            excelRow['Type'] = 'CALCULATED TOTAL';
            excelRow['Adjustment Code'] = '';
            excelRow['Description'] = row.AdjustmentName;
          } else {
            excelRow['Type'] = 'ADJUSTMENT';
            excelRow['Adjustment Code'] = row.AdjustmentCode || '';
            excelRow['Description'] = row.AdjustmentName || '';
          }

          // Add entity columns with their values
          uniqueEntities.forEach((entityCode) => {
            // Get entity name for better column header
            const entity = masterEntities.find((e) => e.EntityCode === entityCode);
            const entityName = entity?.EntityName || entityCode;
            const columnHeader = `${entityCode} - ${entityName}`;

            // Get the value for this entity
            const value = row[entityCode];
            excelRow[columnHeader] = value !== null && value !== undefined ? value : 0;
          });

          return excelRow;
        });
      }





      const ws = window.XLSX.utils.json_to_sheet(excelData);


      // Auto-size columns
      const colWidths = [];
      if (excelData.length > 0) {
        Object.keys(excelData[0]).forEach((key, index) => {
          const maxLength = Math.max(
            key.length,
            ...excelData.map((row) => String(row[key] || '').length)
          );
          colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
        });
        ws['!cols'] = colWidths;
      }


      const wb = window.XLSX.utils.book_new();
      const sheetName = calculationType === 'deferred-rollforward' ? 'Deferred Rollforward' : 'Tax Calculations';
      window.XLSX.utils.book_append_sheet(wb, ws, sheetName);



      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });



      const blob = new Blob([wbout], { type: 'application/octet-stream' });


      const fileName = calculationType === 'deferred-rollforward' ?
      'deferred-rollforward-report.xlsx' :
      'tax-calculations-report.xlsx';


      window.saveAs(blob, fileName);


      setNotification({ open: true, message: 'Export successful', severity: 'success' });

    } catch (error) {
      console.error('=== Excel Export Debug End - ERROR ===');
      console.error('Export error details:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setNotification({
        open: true,
        message: `Failed to export reports: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();
  const { selectedTag } = useTagFilter();
  const { selectedCurrency } = useCurrencyFilter();
  const { selectedSubtag } = useSubtagFilter();
  const { reportsCalculationType: calculationType } = useCalculationType();

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityGroups, setEntityGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [masterEntities, setMasterEntities] = useState([]);
  const [masterAdjustments, setMasterAdjustments] = useState([]);
  const [entityInputs, setEntityInputs] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [entityInputLoading, setEntityInputLoading] = useState(false);
  const [deferredViewMode, setDeferredViewMode] = useState('unit-breakout'); // 'unit-breakout' or 'consolidated'
  const [columnTypeFilter, setColumnTypeFilter] = useState('both'); // 'gross', 'tax-effected', 'both'
  const [showDeferredETR, setShowDeferredETR] = useState(true); // Show Deferred Effective Tax Rate columns
  const [collapsedSections, setCollapsedSections] = useState(new Set()); // Track collapsed sections

  // Auto-disable Deferred ETR when Gross is selected
  useEffect(() => {
    if (columnTypeFilter === 'gross') {
      setShowDeferredETR(false);
    }
  }, [columnTypeFilter]);

  // Handle section collapse/expand
  const handleSectionClick = (sectionId) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master entities:', error);
      setNotification({ open: true, message: 'Failed to fetch entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  // Fetch entity groups
  const fetchEntityGroups = async () => {
    try {
      const response = await api.get('/entity-groups');
      if (response.data && response.data.data) {
        setEntityGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching entity groups:', error);
    }
  };

  // Fetch master adjustments (for lookup purposes)
  const fetchMasterAdjustments = async () => {
    setAdjustmentLoading(true);
    try {
      const response = await api.get('/adjustments/master');
      if (response.data && response.data.data) {
        setMasterAdjustments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master adjustments:', error);
      setNotification({ open: true, message: 'Failed to fetch master adjustments', severity: 'error' });
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Helper function to get tax rate for an entity from EntityInput
  const getTaxRateForEntity = (entityCode, scenarioCode = null) => {
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && (
    !scenarioCode || ei.ScenarioCode === scenarioCode)
    );

    // Get CurrentTaxRate from EntityInput, default to 0 if not found
    const taxRate = entityInput?.CurrentTaxRate || 0;
    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Helper function to get tax rate for a specific activity type in deferred rollforward
  const getTaxRateForActivityType = (entityCode, activityType, scenarioCode = null) => {
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && (
    !scenarioCode || ei.ScenarioCode === scenarioCode)
    );

    if (!entityInput) return 0;

    let taxRate = 0;

    // Apply specific tax rate based on activity type
    switch (activityType) {
      case 'BeginningBalance':
        // Beginning balance uses begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'ReturnToProvision':
        // Return to provision uses prior year tax rate, or begin deferred if no prior year
        taxRate = entityInput.PriorCurrentTaxRate || entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'DeferredTrueUp':
      case 'PurchaseAccounting':
        // Deferred true up and purchase accounting use begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'CurrentActivityPL':
        // Current activity uses current tax rate
        taxRate = entityInput.CurrentTaxRate || 0;
        break;

      case 'OCIAdjustment':
      case 'DeferredExpAdjustment':
      case 'BalanceSheetAdjustment':
      case 'CustomDeferredExpAdjustment':
      case 'CustomBalanceSheetAdjustment':
        // All other activity types use end deferred tax rate
        taxRate = entityInput.EndingDeferredTaxRate || 0;
        break;

      default:
        // Default to 0 for unknown activity types or AdjBeginningBalance (which is a total)
        taxRate = 0;
        break;
    }

    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Get FX rate for an entity from EntityInput data based on activity type
  const getFXRateForEntity = (entityCode, activityType) => {
    if (!entityCode) return null;
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
    );
    if (!entityInput) return null;

    let fxRate = null;

    // Determine which rate to use based on activity type
    // Beginning Balance and Return to Provision use Beginning Spot Rate
    if (activityType === 'BeginningBalance' || activityType === 'ReturnToProvision') {
      fxRate = entityInput.BeginningSpotRate;
    }
    // Current Activity PL and EQ use Average FX Rate
    else if (activityType === 'CurrentActivityPL' || activityType === 'CurrentActivityEQ') {
      fxRate = entityInput.AverageFXRate;
    }
    // All other activity types use Ending Spot Rate
    else {
      fxRate = entityInput.EndingSpotRate;
    }

    if (!fxRate) return null;

    fxRate = parseFloat(fxRate);
    // If FX rate is greater than 1, assume it's stored as percentage and convert to decimal
    if (fxRate > 1) {
      fxRate = fxRate / 100;
    }
    return fxRate;
  };

  // Fetch entity inputs (for current tax rates)
  const fetchEntityInputs = async () => {
    if (!selectedScenario) {
      return;
    }

    setEntityInputLoading(true);
    try {
      const response = await api.get('/entity-models/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      if (response.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setEntityInputs(data);
      }
    } catch (error) {
      console.error('Error fetching entity inputs:', error);
      setNotification({ open: true, message: 'Failed to fetch entity inputs', severity: 'error' });
    } finally {
      setEntityInputLoading(false);
    }
  };

  // Fetch tax calculations data (from adjustment inputs)
  const fetchTaxCalculations = async () => {
    if (!selectedScenario) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const scenarioCode = selectedScenario.scenarioCode;
      // Get all adjustment types from the route map (excluding 'All')
      const adjustmentTypes = Object.keys(adjustmentTypeToRouteMap);
      const allPromises = [];

      // Fetch from each adjustment endpoint with scenario filter
      for (const type of adjustmentTypes) {
        const routePath = adjustmentTypeToRouteMap[type];
        if (routePath) {
          const promise = api.get(`/adjustments/${routePath}`, {
            params: { scenarioCode }
          }).
          then((response) => {
            const data = response.data;
            const adjustments = Array.isArray(data) ? data : data.data || [];
            return adjustments.map((adj) => ({
              ...adj,
              AdjustmentType: type // Add the adjustment type for reference
            }));
          }).
          catch((error) => {
            console.error(`Error fetching ${type} adjustments:`, error);
            return []; // Return empty array on error
          });
          allPromises.push(promise);
        }
      }

      // Wait for all promises to resolve
      const results = await Promise.allSettled(allPromises);

      // Combine all successful results
      let fetchedAdjustments = results.
      filter((result) => result.status === 'fulfilled').
      flatMap((result) => result.value);

      // Add debug logging for GS-T020

      const gsT020Adjustments = fetchedAdjustments.filter((adj) => adj.AdjustmentCode === 'GS-T020');


      // Transform to tax calculations format with entity and adjustment name lookups
      const calculationsData = fetchedAdjustments.map((adj) => {
        const entity = masterEntities.find((e) => e.EntityCode === adj.EntityCode);
        const adjustment = masterAdjustments.find((a) => a.AdjustmentCode === adj.AdjustmentCode);

        return {
          id: `${adj.AdjustmentType}_${adj.id}`,
          originalId: adj.id, // Preserve the original backend ID for updates
          AdjustmentType: adj.AdjustmentType, // Preserve adjustment type for routing
          EntityCode: adj.EntityCode || '',
          EntityName: entity?.EntityName || '',
          AdjustmentCode: adj.AdjustmentCode || '',
          AdjustmentName: adjustment?.AdjustmentName || '',
          LocalCurrencyInput: parseFloat(adj.Amount) || 0, // LC amount from backend
          USDInput: parseFloat(adj.USDAmount) || 0, // USD amount from backend
          TaxEffectedLocalAmount: (parseFloat(adj.Amount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on LC
          TaxEffectedUSDAmount: (parseFloat(adj.USDAmount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on USD
          AdjustmentSource: adj.AdjustmentType || 'Manual',
          ScenarioCode: adj.ScenarioCode || '',
          Tag: adj.Tag || '',
          ActivityType: adj.ActivityType || '', // Preserve ActivityType from original data
          // Preserve all original fields for backend updates
          Amount: adj.Amount,
          USDAmount: adj.USDAmount,
          SubEntity: adj.SubEntity,
          ModifiedBy: adj.ModifiedBy || 'admin',
          AdjustmentId: adj.AdjustmentId
        };
      });

      setAllRows(calculationsData);
      setRows(calculationsData);
    } catch (error) {
      console.error('Error fetching tax calculations:', error);
      setNotification({ open: true, message: 'Failed to fetch tax calculations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      checkLibraryAvailability();

      // Fetch master data in parallel
      await Promise.all([
      fetchMasterEntities(),
      fetchEntityGroups(),
      fetchMasterAdjustments()]
      );
    };

    initializeData();
  }, []);

  useEffect(() => {
    const loadScenarioData = async () => {
      if (!selectedScenario) {
        setLoading(false);
        return;
      }

      setLoading(true);
      await fetchEntityInputs();
    };

    loadScenarioData();
  }, [selectedScenario]);

  useEffect(() => {
    if (masterEntities.length > 0 && masterAdjustments.length > 0) {
      fetchTaxCalculations();
    } else if (!selectedScenario) {
      setLoading(false);
    }
  }, [masterEntities, masterAdjustments, entityInputs]);

  // Section mapping for current provision and effective tax rate (excludes deferred-only sections)
  const currentProvisionSectionMapping = {
    'Pretax': 'PRETAX BOOK INCOME',
    'GS Permanent': 'GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
    'TI Permanent': 'TAXABLE INCOME PERM ADJUSTMENT',
    'TI Temporary': 'TAXABLE INCOME TEMP ADJUSTMENT',
    'Net Operating Loss': 'NET OPERATING LOSS',
    'Tax Adj. Return': 'RETURN BASIS TAX ADJUSTMENTS',
    'Tax Adj. Provision': 'PROVISION BASIS TAX ADJUSTMENTS',
    'Tax Adj. RTP': 'RETURN TO PROVISION TAX ADJUSTMENTS'
  };

  // Complete section mapping including deferred-only sections (for reference only, not used in pivot)
  const sectionMapping = {
    ...currentProvisionSectionMapping,
    'Tax Effected Temporary': 'TAX EFFECTED TEMPORARY DIFFERENCES',
    'Valuation Allowance': 'VALUATION ALLOWANCE',
    'Tax Effected Valuation Allowance': 'TAX EFFECTED VALUATION ALLOWANCE'
  };

  // Section total titles - customize each section's total label here
  const sectionTotalTitles = {
    'Pretax': 'GAAP PRETAX INCOME',
    'GS Permanent': 'Total GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'Total GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'Total STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'TOTAL DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'Total STAT TO TAX TEMPORARY DIFFERENCES',
    'TI Permanent': 'TOTAL TAXABLE INCOME PERM ADJUSTMENT',
    'TI Temporary': 'TOTAL TAXABLE INCOME TEMP ADJUSTMENT',
    'Net Operating Loss': 'TOTAL NET OPERATING LOSS',
    'Tax Effected Temporary': 'TOTAL TAX EFFECTED TEMPORARY DIFFERENCES',
    'Valuation Allowance': 'TOTAL VALUATION ALLOWANCE',
    'Tax Effected Valuation Allowance': 'TOTAL TAX EFFECTED VALUATION ALLOWANCE',
    'Tax Adj. Return': 'TOTAL RETURN BASIS TAX ADJUSTMENTS',
    'Tax Adj. Provision': 'TOTAL PROVISION BASIS TAX ADJUSTMENTS',
    'Tax Adj. RTP': 'TOTAL RETURN TO PROVISION TAX ADJUSTMENTS'
  };

  // Create deferred rollforward table structure
  const createDeferredRollforwardData = (data) => {
    const deferredRows = [];

    // Get unique entities from the data
    const uniqueEntities = [...new Set(data.map((row) => row.EntityCode))].
    filter((code) => code).
    sort();

    // Mapping of adjustment types to deferred rollforward categories
    const categoryMapping = {
      'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
      'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
      'TI Temporary': 'TAXABLE INCOME TEMP ADJUSTMENT',
      'Net Operating Loss': 'NET OPERATING LOSS',
      'Valuation Allowance': 'VALUATION ALLOWANCE',
      'Tax Effected Temporary': 'TAX EFFECTED TEMPORARY DIFFERENCES',
      'Tax Effected Valuation Allowance': 'TAX EFFECTED VALUATION ALLOWANCE'
    };

    // Create rows for each entity and category combination
    uniqueEntities.forEach((entityCode) => {
      DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
        // Find corresponding adjustment type for this category
        const adjustmentType = Object.keys(categoryMapping).find((key) =>
        categoryMapping[key] === header
        );

        if (adjustmentType) {
          // Get adjustments for this category and entity
          const categoryData = data.filter((row) =>
          (row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType) &&
          row.EntityCode === entityCode
          );

          // Only create section if there is data for this category and entity
          if (categoryData.length === 0) {
            return; // Skip this section if no data
          }

          // Add section header with entity code
          const sectionHeaderRow = {
            id: `section-${entityCode}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: `${entityCode} - ${header}`,
            isSection: true
          };
          ACTIVITY_TYPES.forEach((activityType) => {
            sectionHeaderRow[activityType] = null;
            sectionHeaderRow[`${activityType}_TaxEffected`] = null;
          });
          deferredRows.push(sectionHeaderRow);

          // Get unique adjustments in this category for this entity (group by AdjustmentCode)
          const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].
          filter((code) => code).
          sort();

          // Add adjustment rows for this category and entity
          uniqueAdjustments.forEach((adjustmentCode) => {
            const adjustmentData = categoryData.find((row) =>
            row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
            );

            if (adjustmentData) {
              const adjustmentName = adjustmentData.AdjustmentName || '';

              const adjustmentRow = {
                id: `${adjustmentType}-${entityCode}-${adjustmentCode}`,
                EntityCode: entityCode,
                AdjustmentCode: adjustmentCode,
                AdjustmentName: adjustmentName,
                isSection: false
              };

              // Initialize all activity type columns to 0 (both regular and tax effected)
              ACTIVITY_TYPES.forEach((activityType) => {
                adjustmentRow[activityType] = 0;
                adjustmentRow[`${activityType}_TaxEffected`] = 0;
              });

              // Find all adjustments with this entity/adjustment combination and populate based on ActivityType
              // Each unique adjustment (by id or composite key) should only be counted once
              const allMatchingAdjustments = categoryData.filter((row) =>
              row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
              );

              allMatchingAdjustments.forEach((adj) => {
                const activityType = adj.ActivityType;


                if (activityType && ACTIVITY_TYPES.includes(activityType)) {
                  const amount = selectedCurrency === 'USD' ?
                  parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
                  parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

                  // Always store LC amount for CTA calculations (CTA must use LC values regardless of display currency)
                  const lcAmount = parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;




                  // For TAX EFFECTED TEMPORARY DIFFERENCES, VALUATION ALLOWANCE, and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank
                  if (adjustmentType !== 'Tax Effected Temporary' && adjustmentType !== 'Valuation Allowance' && adjustmentType !== 'Tax Effected Valuation Allowance') {

                    adjustmentRow[activityType] += amount;
                    // Store LC values separately for CTA calculation
                    if (!adjustmentRow[`${activityType}_LC`]) adjustmentRow[`${activityType}_LC`] = 0;
                    adjustmentRow[`${activityType}_LC`] += lcAmount;
                  } else {

                  }

                  // Calculate tax effected amount
                  let taxEffectedAmount;
                  let taxEffectedLCAmount;
                  if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Tax Effected Valuation Allowance') {
                    // For tax-effected types, the amount is already tax-effected, so use it directly
                    taxEffectedAmount = amount;
                    taxEffectedLCAmount = lcAmount;
                  } else {
                    // For other types, apply activity-specific tax rate
                    const activityTaxRate = getTaxRateForActivityType(entityCode, activityType, selectedScenario?.scenarioCode);
                    taxEffectedAmount = amount * activityTaxRate;
                    taxEffectedLCAmount = lcAmount * activityTaxRate;
                  }
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  // Store LC tax-effected values separately for CTA calculation
                  if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                  adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                } else {

                }
              });

              // Calculate Adj. Beginning Balance (sum of Beginning Balance + Return to Provision + Deferred True-Up + Purchase Accounting)
              const beginningBalance = adjustmentRow['BeginningBalance'] || 0;
              const returnToProvision = adjustmentRow['ReturnToProvision'] || 0;
              const deferredTrueUp = adjustmentRow['DeferredTrueUp'] || 0;
              const purchaseAccounting = adjustmentRow['PurchaseAccounting'] || 0;

              // Calculate Adj. Beginning Balance (pretax)
              adjustmentRow['AdjBeginningBalance'] = beginningBalance + returnToProvision + deferredTrueUp + purchaseAccounting;

              // Calculate Adj. Beginning Balance (tax effected) - sum of tax effected components
              // Note: Each component already has the correct tax rate applied in the loop above
              adjustmentRow['AdjBeginningBalance_TaxEffected'] =
              (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (
              adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
              adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (
              adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);

              // Calculate Adj. Beginning Balance using LC values for CTA calculation
              const beginningBalanceLC = adjustmentRow['BeginningBalance_LC'] || 0;
              const returnToProvisionLC = adjustmentRow['ReturnToProvision_LC'] || 0;
              const deferredTrueUpLC = adjustmentRow['DeferredTrueUp_LC'] || 0;
              const purchaseAccountingLC = adjustmentRow['PurchaseAccounting_LC'] || 0;
              const adjBeginningBalanceLC = beginningBalanceLC + returnToProvisionLC + deferredTrueUpLC + purchaseAccountingLC;

              // Calculate Adj. Beginning Balance (tax effected) using LC values for CTA calculation
              const adjBeginningBalanceTaxEffectedLC =
              (adjustmentRow['BeginningBalance_TaxEffected_LC'] || 0) + (
              adjustmentRow['ReturnToProvision_TaxEffected_LC'] || 0) + (
              adjustmentRow['DeferredTrueUp_TaxEffected_LC'] || 0) + (
              adjustmentRow['PurchaseAccounting_TaxEffected_LC'] || 0);

              // Calculate CTA Adjustment using the specified formula
              // CTA Adjustment (Pretax) = [(Beginning Balance (pretax)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (pretax)) x (End Spot Rate – Average FX Rate)]
              // CTA Adjustment (Tax Effected) = [(Beginning Balance (tax effected)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (tax effected)) x (End Spot Rate – Average FX Rate)]
              // NOTE: CTA calculations ALWAYS use LC values from adjustment inputs, regardless of display currency

              // Get FX rates from EntityInput data for this entity
              const entityInput = entityInputs.find(
                (ei) => ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
              );
              const toIndirectFxRate = (rate) => {
                if (rate == null || rate === '') return 1.0;
                const parsed = parseFloat(rate);
                if (!Number.isFinite(parsed) || parsed === 0) return 0;
                return 1 / parsed;
              };

              const beginSpotRate = toIndirectFxRate(entityInput?.BeginningSpotRate || 1.0);
              const endSpotRate = toIndirectFxRate(entityInput?.EndingSpotRate || 1.0);
              const averageFXRate = toIndirectFxRate(entityInput?.AverageFXRate || 1.0);

              // Calculate Pretax CTA Adjustment using LC values
              // Beginning balance for pretax = Adj. Beginning Balance (pretax) in LC
              const pretaxBeginningBalanceLC = adjBeginningBalanceLC;

              // Current activity for pretax = all columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
              const pretaxCurrentActivityLC =
              (adjustmentRow['CurrentActivityPL_LC'] || 0) + (
              adjustmentRow['OCIAdjustment_LC'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_LC'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_LC'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_LC'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_LC'] || 0);

              const pretaxBeginningComponent = pretaxBeginningBalanceLC * (endSpotRate - beginSpotRate);
              const pretaxCurrentComponent = pretaxCurrentActivityLC * (endSpotRate - averageFXRate);
              const ctaAdjustmentPretax = pretaxBeginningComponent + pretaxCurrentComponent;

              // Zero out CTA adjustment when LC currency is selected
              adjustmentRow['CTAAdjustment'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

              // Calculate Tax-Effected CTA Adjustment using LC values
              // Beginning balance for tax-effected = Adj. Beginning Balance (tax effected) in LC
              const taxEffectedBeginningBalanceLC = adjBeginningBalanceTaxEffectedLC;

              // Current activity for tax-effected = all tax-effected columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
              const taxEffectedCurrentActivityLC =
              (adjustmentRow['CurrentActivityPL_TaxEffected_LC'] || 0) + (
              adjustmentRow['OCIAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected_LC'] || 0);

              const taxEffectedBeginningComponent = taxEffectedBeginningBalanceLC * (endSpotRate - beginSpotRate);
              const taxEffectedCurrentComponent = taxEffectedCurrentActivityLC * (endSpotRate - averageFXRate);
              const ctaAdjustmentTaxEffected = taxEffectedBeginningComponent + taxEffectedCurrentComponent;

              // Zero out CTA adjustment when LC currency is selected
              adjustmentRow['CTAAdjustment_TaxEffected'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentTaxEffected;

              // Calculate Ending Balance: Adj. Beginning Balance + all columns between Adj. Beginning Balance and Ending Balance
              // Columns after AdjBeginningBalance: CurrentActivityPL, OCIAdjustment, DeferredExpAdjustment, BalanceSheetAdjustment, CustomDeferredExpAdjustment, CustomBalanceSheetAdjustment, CTAAdjustment

              // Pretax Ending Balance
              const endingBalancePretax =
              (adjustmentRow['AdjBeginningBalance'] || 0) + (
              adjustmentRow['CurrentActivityPL'] || 0) + (
              adjustmentRow['OCIAdjustment'] || 0) + (
              adjustmentRow['DeferredExpAdjustment'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment'] || 0) + (
              adjustmentRow['CTAAdjustment'] || 0);

              // Tax Effected Ending Balance
              const endingBalanceTaxEffected =
              (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (
              adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
              adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CTAAdjustment_TaxEffected'] || 0);

              adjustmentRow['EndingBalance'] = endingBalancePretax;
              adjustmentRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffected;

              // Calculate Tax Rate Change using the specified formula
              // Tax Rate Change = [(pretax Beginning balance + pretax deferred true up + pretax purchase accounting) x (Ending deferred rate - Beginning deferred rate)] 
              //                 + [pretax return to provision x (Ending deferred rate – Prior current rate)]
              //                 + [pretax Current year activity x (Ending deferred rate - Current rate)]  
              //                 + [(pretax deferred exp adjustment + pretax balance sheet adjustment + pretax OCI + pretax custom deferred adjustment + pretax custom balance sheet only adjustment) x (Ending deferred rate - Current rate)]

              // Get tax rates from EntityInput data for this entity
              const entityInputForTaxRate = entityInputs.find(
                (ei) => ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
              );

              // Get deferred and current tax rates (convert from percentage if needed)
              let endingDeferredRate = entityInputForTaxRate?.EndingDeferredTaxRate || 0;
              endingDeferredRate = endingDeferredRate > 1 ? endingDeferredRate / 100 : endingDeferredRate;

              let beginningDeferredRate = entityInputForTaxRate?.BeginningDeferredTaxRate || 0;
              beginningDeferredRate = beginningDeferredRate > 1 ? beginningDeferredRate / 100 : beginningDeferredRate;

              let priorCurrentRate = entityInputForTaxRate?.PriorCurrentTaxRate || 0;
              priorCurrentRate = priorCurrentRate > 1 ? priorCurrentRate / 100 : priorCurrentRate;

              let currentRate = entityInputForTaxRate?.CurrentTaxRate || 0;
              currentRate = currentRate > 1 ? currentRate / 100 : currentRate;

              // Component 1: Beginning balance items
              const beginningBalanceForTaxRate = adjustmentRow['BeginningBalance'] || 0;
              const deferredTrueUpForTaxRate = adjustmentRow['DeferredTrueUp'] || 0;
              const purchaseAccountingForTaxRate = adjustmentRow['PurchaseAccounting'] || 0;
              const taxRateComponent1 = (beginningBalanceForTaxRate + deferredTrueUpForTaxRate + purchaseAccountingForTaxRate) * (endingDeferredRate - beginningDeferredRate);

              // Component 2: Return to provision
              const returnToProvisionForTaxRate = adjustmentRow['ReturnToProvision'] || 0;
              const taxRateComponent2 = returnToProvisionForTaxRate * (endingDeferredRate - priorCurrentRate);

              // Component 3: Current year activity
              const currentYearActivityForTaxRate = adjustmentRow['CurrentActivityPL'] || 0;
              const taxRateComponent3 = currentYearActivityForTaxRate * (endingDeferredRate - currentRate);

              // Component 4: Other adjustments
              const deferredExpAdjForTaxRate = adjustmentRow['DeferredExpAdjustment'] || 0;
              const balanceSheetAdjForTaxRate = adjustmentRow['BalanceSheetAdjustment'] || 0;
              const ociForTaxRate = adjustmentRow['OCIAdjustment'] || 0;
              const customDeferredAdjForTaxRate = adjustmentRow['CustomDeferredExpAdjustment'] || 0;
              const customBalanceSheetAdjForTaxRate = adjustmentRow['CustomBalanceSheetAdjustment'] || 0;
              const taxRateComponent4 = (deferredExpAdjForTaxRate + balanceSheetAdjForTaxRate + ociForTaxRate + customDeferredAdjForTaxRate + customBalanceSheetAdjForTaxRate) * (endingDeferredRate - currentRate);

              // Calculate total Tax Rate Change
              const taxRateChange = taxRateComponent1 + taxRateComponent2 + taxRateComponent3 + taxRateComponent4;

              adjustmentRow['TaxRateChange_TaxEffected'] = taxRateChange;
              adjustmentRow['TaxRateChange'] = -taxRateChange;

              // Note: ReturnToProvision and DeferredTrueUp pretax columns should keep their original pretax values
              // They are already populated correctly in the activity type loop above
              // Do NOT overwrite them with tax-effected values

              // Current Activity - actual tax effected value
              adjustmentRow['CurrentActivity'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);

              // Write-Off = (Deferred Exp Adjustment + Custom Deferred Exp Adjustment) tax effected
              const deferredExpAdjustmentTaxEffected = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
              const customDeferredExpAdjustmentTaxEffected = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
              adjustmentRow['WriteOff'] = -(deferredExpAdjustmentTaxEffected + customDeferredExpAdjustmentTaxEffected);

              // Create dedicated rollforward columns with actual tax-effected values
              adjustmentRow['ReturnToProvisionRollforward'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
              adjustmentRow['DeferredTrueUpRollforward'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);

              // Use the displayed column values (actual tax-effected, no pre-negation) for formula components
              const taxRateChangeDisplay = adjustmentRow['TaxRateChange'] || 0;
              const returnToProvisionDisplay = adjustmentRow['ReturnToProvisionRollforward'] || 0;
              const deferredTrueUpDisplay = adjustmentRow['DeferredTrueUpRollforward'] || 0;
              const currentActivityDisplay = adjustmentRow['CurrentActivity'] || 0;
              const writeOffDisplay = adjustmentRow['WriteOff'] || 0;

              if (adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
                // ValuationAllowance = -(Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity)
                const valuationAllowanceValue = -(taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay);
                adjustmentRow['ValuationAllowance'] = valuationAllowanceValue;
                // Deferred Expense equals ValuationAllowance
                adjustmentRow['DeferredExpense'] = valuationAllowanceValue;
              } else {
                // DeferredExpense = Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity + Write-Off + Valuation Allowance
                adjustmentRow['DeferredExpense'] = taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay + writeOffDisplay;
                adjustmentRow['ValuationAllowance'] = 0;
              }

              deferredRows.push(adjustmentRow);
            }
          });
        }

        // Add section total row for this entity and category
        const sectionTotalRow = {
          id: `total-${entityCode}-${headerIndex}`,
          EntityCode: '',
          AdjustmentCode: '',
          AdjustmentName: `TOTAL ${header}`,
          isSubFooter: true
        };

        // Calculate totals for each activity type from the section's adjustment rows
        ACTIVITY_TYPES.forEach((activityType) => {
          let total = 0;
          let taxEffectedTotal = 0;
          // Find all adjustment rows added for this section (since the last section header)
          const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
          const sectionRows = deferredRows.slice(sectionStartIndex + 1);

          sectionRows.forEach((row) => {
            if (!row.isSection && !row.isSubFooter) {
              total += parseFloat(row[activityType]) || 0;
              taxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
            }
          });

          // For TAX EFFECTED TEMPORARY DIFFERENCES, VALUATION ALLOWANCE, and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank in totals
          if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
            sectionTotalRow[activityType] = null;
          } else {
            sectionTotalRow[activityType] = total;
          }
          sectionTotalRow[`${activityType}_TaxEffected`] = taxEffectedTotal;
        });

        // Calculate totals for CTA Adjustment and Ending Balance columns
        let ctaAdjustmentTotal = 0;
        let ctaAdjustmentTaxEffectedTotal = 0;
        let endingBalanceTotal = 0;
        let endingBalanceTaxEffectedTotal = 0;

        const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
        const sectionRows = deferredRows.slice(sectionStartIndex + 1);

        // Calculate totals for new columns
        let deferredExpenseTotal = 0;
        let taxRateChangeTotal = 0;
        let taxRateChangeTaxEffectedTotal = 0;
        let returnToProvisionTotal = 0;
        let deferredTrueUpTotal = 0;
        let returnToProvisionRollforwardTotal = 0;
        let deferredTrueUpRollforwardTotal = 0;
        let currentActivityTotal = 0;
        let writeOffTotal = 0;
        let valuationAllowanceTotal = 0;

        sectionRows.forEach((row) => {
          if (!row.isSection && !row.isSubFooter) {
            ctaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
            ctaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
            endingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
            endingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;

            // Add totals for new columns
            deferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
            taxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
            taxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
            returnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
            deferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
            returnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
            deferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
            currentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
            writeOffTotal += parseFloat(row['WriteOff']) || 0;
            valuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
          }
        });

        sectionTotalRow['CTAAdjustment'] = ctaAdjustmentTotal;
        sectionTotalRow['CTAAdjustment_TaxEffected'] = ctaAdjustmentTaxEffectedTotal;
        sectionTotalRow['EndingBalance'] = endingBalanceTotal;
        sectionTotalRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffectedTotal;

        // Set totals for new columns
        sectionTotalRow['DeferredExpense'] = deferredExpenseTotal;
        sectionTotalRow['TaxRateChange'] = taxRateChangeTotal;
        sectionTotalRow['TaxRateChange_TaxEffected'] = taxRateChangeTaxEffectedTotal;

        // For TAX EFFECTED types, keep pretax calculated columns blank in totals
        if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
          sectionTotalRow['ReturnToProvision'] = null;
          sectionTotalRow['DeferredTrueUp'] = null;
          sectionTotalRow['CurrentActivity'] = null;
        } else {
          sectionTotalRow['ReturnToProvision'] = returnToProvisionTotal;
          sectionTotalRow['DeferredTrueUp'] = deferredTrueUpTotal;
          sectionTotalRow['CurrentActivity'] = currentActivityTotal;
        }

        sectionTotalRow['ReturnToProvisionRollforward'] = returnToProvisionRollforwardTotal;
        sectionTotalRow['DeferredTrueUpRollforward'] = deferredTrueUpRollforwardTotal;
        sectionTotalRow['WriteOff'] = writeOffTotal;
        sectionTotalRow['ValuationAllowance'] = valuationAllowanceTotal;

        deferredRows.push(sectionTotalRow);
      });

      // Add grand total row after all categories for this entity
      const grandTotalRow = {
        id: `grand-total-${entityCode}`,
        EntityCode: '',
        AdjustmentCode: '',
        AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
        isSubFooter: true
      };

      // Calculate grand totals for each activity type from all sections for this entity
      ACTIVITY_TYPES.forEach((activityType) => {
        let grandTotal = 0;
        let grandTaxEffectedTotal = 0;
        let currentSectionType = null;

        // Find all adjustment rows for this entity (not section headers or totals)
        deferredRows.forEach((row) => {
          // Track current section type
          if (row.isSection && row.AdjustmentName && row.AdjustmentName.includes(entityCode)) {
            // Extract section type from section header
            if (row.AdjustmentName.includes('TAX EFFECTED TEMPORARY DIFFERENCES')) {
              currentSectionType = 'Tax Effected Temporary';
            } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE') && row.AdjustmentName.includes('TAX EFFECTED')) {
              currentSectionType = 'Tax Effected Valuation Allowance';
            } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE')) {
              currentSectionType = 'Valuation Allowance';
            } else {
              currentSectionType = 'Other';
            }
          }

          if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
            // For TAX EFFECTED sections and VALUATION ALLOWANCE sections, don't include (pretax) columns in grand total
            if (currentSectionType !== 'Tax Effected Temporary' && currentSectionType !== 'Valuation Allowance' && currentSectionType !== 'Tax Effected Valuation Allowance') {
              grandTotal += parseFloat(row[activityType]) || 0;
            }
            grandTaxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
          }
        });

        grandTotalRow[activityType] = grandTotal;
        grandTotalRow[`${activityType}_TaxEffected`] = grandTaxEffectedTotal;
      });

      // Calculate grand totals for CTA Adjustment, Ending Balance, and new columns
      let grandCtaAdjustmentTotal = 0;
      let grandCtaAdjustmentTaxEffectedTotal = 0;
      let grandEndingBalanceTotal = 0;
      let grandEndingBalanceTaxEffectedTotal = 0;
      let grandDeferredExpenseTotal = 0;
      let grandTaxRateChangeTotal = 0;
      let grandTaxRateChangeTaxEffectedTotal = 0;
      let grandReturnToProvisionTotal = 0;
      let grandDeferredTrueUpTotal = 0;
      let grandReturnToProvisionRollforwardTotal = 0;
      let grandDeferredTrueUpRollforwardTotal = 0;
      let grandCurrentActivityTotal = 0;
      let grandWriteOffTotal = 0;
      let grandValuationAllowanceTotal = 0;

      deferredRows.forEach((row) => {
        if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
          grandCtaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
          grandCtaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
          grandEndingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
          grandEndingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;

          // Add grand totals for new columns
          grandDeferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
          grandTaxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
          grandTaxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
          grandReturnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
          grandDeferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
          grandReturnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
          grandDeferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
          grandCurrentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
          grandWriteOffTotal += parseFloat(row['WriteOff']) || 0;
          grandValuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
        }
      });

      grandTotalRow['CTAAdjustment'] = grandCtaAdjustmentTotal;
      grandTotalRow['CTAAdjustment_TaxEffected'] = grandCtaAdjustmentTaxEffectedTotal;
      grandTotalRow['EndingBalance'] = grandEndingBalanceTotal;
      grandTotalRow['EndingBalance_TaxEffected'] = grandEndingBalanceTaxEffectedTotal;

      // Set grand totals for new columns
      grandTotalRow['DeferredExpense'] = grandDeferredExpenseTotal;
      grandTotalRow['TaxRateChange'] = grandTaxRateChangeTotal;
      grandTotalRow['TaxRateChange_TaxEffected'] = grandTaxRateChangeTaxEffectedTotal;
      grandTotalRow['ReturnToProvision'] = grandReturnToProvisionTotal;
      grandTotalRow['DeferredTrueUp'] = grandDeferredTrueUpTotal;
      grandTotalRow['ReturnToProvisionRollforward'] = grandReturnToProvisionRollforwardTotal;
      grandTotalRow['DeferredTrueUpRollforward'] = grandDeferredTrueUpRollforwardTotal;
      grandTotalRow['CurrentActivity'] = grandCurrentActivityTotal;
      grandTotalRow['WriteOff'] = grandWriteOffTotal;
      grandTotalRow['ValuationAllowance'] = grandValuationAllowanceTotal;

      deferredRows.push(grandTotalRow);
    });

    return { deferredRows, activityTypes: ACTIVITY_TYPES };
  };

  // Create consolidated deferred rollforward table structure (grouped by adjustment type, not entity)
  const createConsolidatedDeferredRollforwardData = (data) => {
    const deferredRows = [];

    // Mapping of adjustment types to deferred rollforward categories
    const categoryMapping = {
      'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
      'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
      'TI Temporary': 'TAXABLE INCOME TEMP ADJUSTMENT',
      'Net Operating Loss': 'NET OPERATING LOSS',
      'Valuation Allowance': 'VALUATION ALLOWANCE',
      'Tax Effected Temporary': 'TAX EFFECTED TEMPORARY DIFFERENCES',
      'Tax Effected Valuation Allowance': 'TAX EFFECTED VALUATION ALLOWANCE'
    };

    // Process each category in the order of DEFERRED_ROLLFORWARD_HEADERS
    DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
      // Add section header
      const sectionHeaderRow = {
        id: `section-${headerIndex}`,
        EntityCode: '',
        AdjustmentCode: '',
        AdjustmentName: header,
        isSection: true
      };
      ACTIVITY_TYPES.forEach((activityType) => {
        sectionHeaderRow[activityType] = null;
        sectionHeaderRow[`${activityType}_TaxEffected`] = null;
      });
      deferredRows.push(sectionHeaderRow);

      // Find corresponding adjustment type for this category
      const adjustmentType = Object.keys(categoryMapping).find((key) =>
      categoryMapping[key] === header
      );

      if (adjustmentType) {
        // Get all adjustments for this category across all entities
        const categoryData = data.filter((row) =>
        row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType
        );

        // Group by unique entity/adjustment combinations (ignoring tag/subtag/subentity)
        const entityAdjustmentGroups = {};
        categoryData.forEach((row) => {
          const key = `${row.EntityCode}_${row.AdjustmentCode}`;
          if (!entityAdjustmentGroups[key]) {
            entityAdjustmentGroups[key] = [];
          }
          entityAdjustmentGroups[key].push(row);
        });

        // Sort keys for consistent ordering
        const sortedKeys = Object.keys(entityAdjustmentGroups).sort((a, b) => {
          const [entityA, adjCodeA] = a.split('_');
          const [entityB, adjCodeB] = b.split('_');
          if (entityA !== entityB) {
            return entityA.localeCompare(entityB);
          }
          return adjCodeA.localeCompare(adjCodeB);
        });

        // Add one row per entity/adjustment combination, summing all matching adjustments
        sortedKeys.forEach((key) => {
          const adjustmentsGroup = entityAdjustmentGroups[key];
          const firstAdj = adjustmentsGroup[0];

          const adjustmentRow = {
            id: `${adjustmentType}-${firstAdj.EntityCode}-${firstAdj.AdjustmentCode}`,
            EntityCode: firstAdj.EntityCode,
            AdjustmentCode: firstAdj.AdjustmentCode,
            AdjustmentName: firstAdj.AdjustmentName || '',
            isSection: false
          };

          // Initialize all activity type columns to 0 (both regular and tax effected)
          ACTIVITY_TYPES.forEach((activityType) => {
            adjustmentRow[activityType] = 0;
            adjustmentRow[`${activityType}_TaxEffected`] = 0;
          });

          // Sum all adjustments in this group across all activity types
          adjustmentsGroup.forEach((adj) => {
            const activityType = adj.ActivityType;

            if (activityType && ACTIVITY_TYPES.includes(activityType)) {
              const amount = selectedCurrency === 'USD' ?
              parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
              parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

              // Always store LC amount for CTA calculations (CTA must use LC values regardless of display currency)
              const lcAmount = parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;



              // For TAX EFFECTED TEMPORARY DIFFERENCES, VALUATION ALLOWANCE, and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank
              if (adjustmentType !== 'Tax Effected Temporary' && adjustmentType !== 'Valuation Allowance' && adjustmentType !== 'Tax Effected Valuation Allowance') {

                adjustmentRow[activityType] += amount;
                // Store LC values separately for CTA calculation
                if (!adjustmentRow[`${activityType}_LC`]) adjustmentRow[`${activityType}_LC`] = 0;
                adjustmentRow[`${activityType}_LC`] += lcAmount;
              } else {

              }

              // Calculate tax effected amount
              let taxEffectedAmount;
              let taxEffectedLCAmount;
              if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Tax Effected Valuation Allowance') {
                // For tax-effected types, the amount is already tax-effected, so use it directly
                taxEffectedAmount = amount;
                taxEffectedLCAmount = lcAmount;
              } else {
                // For other types, apply activity-specific tax rate
                const activityTaxRate = getTaxRateForActivityType(adj.EntityCode, activityType, selectedScenario?.scenarioCode);
                taxEffectedAmount = amount * activityTaxRate;
                taxEffectedLCAmount = lcAmount * activityTaxRate;
              }
              adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
              // Store LC tax-effected values separately for CTA calculation
              if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
              adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
            }
          });

          // Use the first adjustment's entity for FX rate lookups

          // Calculate Adj. Beginning Balance (sum of Beginning Balance + Return to Provision + Deferred True-Up + Purchase Accounting)
          const beginningBalance = adjustmentRow['BeginningBalance'] || 0;
          const returnToProvision = adjustmentRow['ReturnToProvision'] || 0;
          const deferredTrueUp = adjustmentRow['DeferredTrueUp'] || 0;
          const purchaseAccounting = adjustmentRow['PurchaseAccounting'] || 0;

          // Calculate Adj. Beginning Balance (pretax)
          adjustmentRow['AdjBeginningBalance'] = beginningBalance + returnToProvision + deferredTrueUp + purchaseAccounting;

          // Calculate Adj. Beginning Balance (tax effected) - sum of tax effected components
          adjustmentRow['AdjBeginningBalance_TaxEffected'] =
          (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (
          adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
          adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (
          adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);

          // Calculate Adj. Beginning Balance using LC values for CTA calculation
          const beginningBalanceLC = adjustmentRow['BeginningBalance_LC'] || 0;
          const returnToProvisionLC = adjustmentRow['ReturnToProvision_LC'] || 0;
          const deferredTrueUpLC = adjustmentRow['DeferredTrueUp_LC'] || 0;
          const purchaseAccountingLC = adjustmentRow['PurchaseAccounting_LC'] || 0;
          const adjBeginningBalanceLC = beginningBalanceLC + returnToProvisionLC + deferredTrueUpLC + purchaseAccountingLC;

          // Calculate Adj. Beginning Balance (tax effected) using LC values for CTA calculation
          const adjBeginningBalanceTaxEffectedLC =
          (adjustmentRow['BeginningBalance_TaxEffected_LC'] || 0) + (
          adjustmentRow['ReturnToProvision_TaxEffected_LC'] || 0) + (
          adjustmentRow['DeferredTrueUp_TaxEffected_LC'] || 0) + (
          adjustmentRow['PurchaseAccounting_TaxEffected_LC'] || 0);

          // Calculate CTA Adjustment using the specified formula
          // CTA Adjustment (Pretax) = [(Beginning Balance (pretax)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (pretax)) x (End Spot Rate – Average FX Rate)]
          // CTA Adjustment (Tax Effected) = [(Beginning Balance (tax effected)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (tax effected)) x (End Spot Rate – Average FX Rate)]
          // NOTE: CTA calculations ALWAYS use LC values from adjustment inputs, regardless of display currency

          // Get FX rates from EntityInput data for this entity
          const entityInput = entityInputs.find(
            (ei) => ei.EntityCode === firstAdj.EntityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
          );
          const toIndirectFxRate = (rate) => {
            if (rate == null || rate === '') return 1.0;
            const parsed = parseFloat(rate);
            if (!Number.isFinite(parsed) || parsed === 0) return 0;
            return 1 / parsed;
          };

          const beginSpotRate = toIndirectFxRate(entityInput?.BeginningSpotRate || 1.0);
          const endSpotRate = toIndirectFxRate(entityInput?.EndingSpotRate || 1.0);
          const averageFXRate = toIndirectFxRate(entityInput?.AverageFXRate || 1.0);

          // Calculate Pretax CTA Adjustment using LC values
          const pretaxBeginningBalanceLC = adjBeginningBalanceLC;
          const pretaxCurrentActivityLC =
          (adjustmentRow['CurrentActivityPL_LC'] || 0) + (
          adjustmentRow['OCIAdjustment_LC'] || 0) + (
          adjustmentRow['DeferredExpAdjustment_LC'] || 0) + (
          adjustmentRow['BalanceSheetAdjustment_LC'] || 0) + (
          adjustmentRow['CustomDeferredExpAdjustment_LC'] || 0) + (
          adjustmentRow['CustomBalanceSheetAdjustment_LC'] || 0);

          const pretaxBeginningComponent = pretaxBeginningBalanceLC * (endSpotRate - beginSpotRate);
          const pretaxCurrentComponent = pretaxCurrentActivityLC * (endSpotRate - averageFXRate);
          const ctaAdjustmentPretax = pretaxBeginningComponent + pretaxCurrentComponent;

          // Zero out CTA adjustment when LC currency is selected
          adjustmentRow['CTAAdjustment'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

          // Calculate Tax-Effected CTA Adjustment using LC values
          const taxEffectedBeginningBalanceLC = adjBeginningBalanceTaxEffectedLC;
          const taxEffectedCurrentActivityLC =
          (adjustmentRow['CurrentActivityPL_TaxEffected_LC'] || 0) + (
          adjustmentRow['OCIAdjustment_TaxEffected_LC'] || 0) + (
          adjustmentRow['DeferredExpAdjustment_TaxEffected_LC'] || 0) + (
          adjustmentRow['BalanceSheetAdjustment_TaxEffected_LC'] || 0) + (
          adjustmentRow['CustomDeferredExpAdjustment_TaxEffected_LC'] || 0) + (
          adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected_LC'] || 0);

          const taxEffectedBeginningComponent = taxEffectedBeginningBalanceLC * (endSpotRate - beginSpotRate);
          const taxEffectedCurrentComponent = taxEffectedCurrentActivityLC * (endSpotRate - averageFXRate);
          const ctaAdjustmentTaxEffected = taxEffectedBeginningComponent + taxEffectedCurrentComponent;

          // Zero out CTA adjustment when LC currency is selected
          adjustmentRow['CTAAdjustment_TaxEffected'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentTaxEffected;

          // Calculate Ending Balance: Adj. Beginning Balance + all columns between Adj. Beginning Balance and Ending Balance
          const endingBalancePretax =
          (adjustmentRow['AdjBeginningBalance'] || 0) + (
          adjustmentRow['CurrentActivityPL'] || 0) + (
          adjustmentRow['OCIAdjustment'] || 0) + (
          adjustmentRow['DeferredExpAdjustment'] || 0) + (
          adjustmentRow['BalanceSheetAdjustment'] || 0) + (
          adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
          adjustmentRow['CustomBalanceSheetAdjustment'] || 0) + (
          adjustmentRow['CTAAdjustment'] || 0);

          const endingBalanceTaxEffected =
          (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (
          adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
          adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (
          adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
          adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (
          adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
          adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0) + (
          adjustmentRow['CTAAdjustment_TaxEffected'] || 0);

          adjustmentRow['EndingBalance'] = endingBalancePretax;
          adjustmentRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffected;

          // Calculate Tax Rate Change using FBOS tax rates
          const entityInputForTaxRate = entityInputs.find(
            (ei) => ei.EntityCode === firstAdj.EntityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
          );
          const beginningFBOSTaxRate = entityInputForTaxRate?.BeginningFBOSTaxRate || 0;
          const endingFBOSTaxRate = entityInputForTaxRate?.EndingFBOSTaxRate || 0;
          const currentYearFBOSTaxRate = entityInputForTaxRate?.CurrentYearFBOSTaxRate || 0;

          // Calculate Tax Rate Change components
          const beginningBalanceForTaxRate = adjustmentRow['BeginningBalance'] || 0;
          const returnToProvisionForTaxRate = adjustmentRow['ReturnToProvision'] || 0;
          const deferredTrueUpForTaxRate = adjustmentRow['DeferredTrueUp'] || 0;
          const purchaseAccountingForTaxRate = adjustmentRow['PurchaseAccounting'] || 0;
          const currentYearActivity = (adjustmentRow['CurrentActivityPL'] || 0) + (adjustmentRow['CurrentActivityEQ'] || 0);
          const deferredOnlyForTaxRate = adjustmentRow['DeferredExpAdjustment'] || 0;
          const balanceSheetOnlyForTaxRate = adjustmentRow['BalanceSheetAdjustment'] || 0;
          const ociForTaxRate = adjustmentRow['OCIAdjustment'] || 0;

          // Calculate Tax Rate Change (Tax Effected)
          const taxRateBeginningComponent = (beginningBalanceForTaxRate + returnToProvisionForTaxRate + deferredTrueUpForTaxRate + purchaseAccountingForTaxRate) * (endingFBOSTaxRate - beginningFBOSTaxRate);
          const taxRateCurrentYearComponent = (currentYearActivity + deferredOnlyForTaxRate + balanceSheetOnlyForTaxRate + ociForTaxRate) * (endingFBOSTaxRate - currentYearFBOSTaxRate);

          adjustmentRow['TaxRateChange_TaxEffected'] = taxRateBeginningComponent + taxRateCurrentYearComponent;
          adjustmentRow['TaxRateChange'] = adjustmentRow['TaxRateChange_TaxEffected'] || 0;

          // Set other calculated fields
          // For TAX EFFECTED types, keep these columns blank
          if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
            adjustmentRow['ReturnToProvision'] = null;
            adjustmentRow['CurrentActivity'] = null;
            adjustmentRow['DeferredTrueUp'] = null;
          } else {
            adjustmentRow['ReturnToProvision'] = adjustmentRow['ReturnToProvision_TaxEffected'] || 0;
            adjustmentRow['CurrentActivity'] = (adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (adjustmentRow['CurrentActivityEQ_TaxEffected'] || 0);
            adjustmentRow['DeferredTrueUp'] = adjustmentRow['DeferredTrueUp_TaxEffected'] || 0;
          }

          // Write-Off = (Deferred Exp Adjustment + Custom Deferred Exp Adjustment) tax effected
          const deferredExpAdjTaxEffected = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
          const customDeferredExpAdjTaxEffected = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
          adjustmentRow['WriteOff'] = -(deferredExpAdjTaxEffected + customDeferredExpAdjTaxEffected);

          // Create dedicated rollforward columns with actual tax-effected values
          adjustmentRow['ReturnToProvisionRollforward'] = adjustmentRow['ReturnToProvision_TaxEffected'] || 0;
          adjustmentRow['DeferredTrueUpRollforward'] = adjustmentRow['DeferredTrueUp_TaxEffected'] || 0;

          // Use the displayed column values (actual tax-effected, no pre-negation) for formula components
          const taxRateChangeDisplay = adjustmentRow['TaxRateChange'] || 0;
          const returnToProvisionDisplay = adjustmentRow['ReturnToProvisionRollforward'] || 0;
          const deferredTrueUpDisplay = adjustmentRow['DeferredTrueUpRollforward'] || 0;
          const currentActivityDisplay = adjustmentRow['CurrentActivity'] || 0;
          const writeOffDisplay = adjustmentRow['WriteOff'] || 0;

          if (adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
            // ValuationAllowance = -(Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity)
            const valuationAllowanceValue = -(taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay);
            adjustmentRow['ValuationAllowance'] = valuationAllowanceValue;
            // Deferred Expense equals ValuationAllowance
            adjustmentRow['DeferredExpense'] = valuationAllowanceValue;

            // Hide the 5 specific rollforward columns
            adjustmentRow['WriteOff'] = null;
            adjustmentRow['TaxRateChange'] = null;
            adjustmentRow['ReturnToProvisionRollforward'] = null;
            adjustmentRow['DeferredTrueUpRollforward'] = null;
            adjustmentRow['CurrentActivity'] = null;
          } else {
            // DeferredExpense = Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity + Write-Off + Valuation Allowance
            adjustmentRow['ValuationAllowance'] = 0;
            adjustmentRow['DeferredExpense'] = taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay + writeOffDisplay;
          }

          deferredRows.push(adjustmentRow);
        });
      }

      // Add section total row for this category
      const sectionTotalRow = {
        id: `total-${headerIndex}`,
        EntityCode: '',
        AdjustmentCode: '',
        AdjustmentName: `TOTAL ${header}`,
        isSubFooter: true
      };

      // Calculate totals for each activity type from the section's adjustment rows
      ACTIVITY_TYPES.forEach((activityType) => {
        let total = 0;
        let taxEffectedTotal = 0;
        // Find all adjustment rows added for this section (since the last section header)
        const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
        const sectionRows = deferredRows.slice(sectionStartIndex + 1);

        sectionRows.forEach((row) => {
          if (!row.isSection && !row.isSubFooter) {
            total += parseFloat(row[activityType]) || 0;
            taxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
          }
        });

        sectionTotalRow[activityType] = total;
        sectionTotalRow[`${activityType}_TaxEffected`] = taxEffectedTotal;
      });

      // For TAX EFFECTED TEMPORARY DIFFERENCES, VALUATION ALLOWANCE, and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank in totals
      if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
        ACTIVITY_TYPES.forEach((activityType) => {
          sectionTotalRow[activityType] = null;
        });
      }

      // Calculate totals for other columns
      let ctaAdjustmentTotal = 0;
      let ctaAdjustmentTaxEffectedTotal = 0;
      let endingBalanceTotal = 0;
      let endingBalanceTaxEffectedTotal = 0;
      let deferredExpenseTotal = 0;
      let taxRateChangeTotal = 0;
      let taxRateChangeTaxEffectedTotal = 0;
      let returnToProvisionTotal = 0;
      let deferredTrueUpTotal = 0;
      let returnToProvisionRollforwardTotal = 0;
      let deferredTrueUpRollforwardTotal = 0;
      let currentActivityTotal = 0;
      let writeOffTotal = 0;
      let valuationAllowanceTotal = 0;

      const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
      const sectionRows = deferredRows.slice(sectionStartIndex + 1);

      sectionRows.forEach((row) => {
        if (!row.isSection && !row.isSubFooter) {
          ctaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
          ctaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
          endingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
          endingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
          deferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
          taxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
          taxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
          returnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
          deferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
          returnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
          deferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
          currentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
          writeOffTotal += parseFloat(row['WriteOff']) || 0;
          valuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
        }
      });

      sectionTotalRow['CTAAdjustment'] = ctaAdjustmentTotal;
      sectionTotalRow['CTAAdjustment_TaxEffected'] = ctaAdjustmentTaxEffectedTotal;
      sectionTotalRow['EndingBalance'] = endingBalanceTotal;
      sectionTotalRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffectedTotal;
      sectionTotalRow['DeferredExpense'] = deferredExpenseTotal;
      sectionTotalRow['TaxRateChange'] = taxRateChangeTotal;
      sectionTotalRow['TaxRateChange_TaxEffected'] = taxRateChangeTaxEffectedTotal;

      // For TAX EFFECTED types, keep pretax calculated columns blank in totals
      if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
        sectionTotalRow['ReturnToProvision'] = null;
        sectionTotalRow['DeferredTrueUp'] = null;
        sectionTotalRow['CurrentActivity'] = null;
      } else {
        sectionTotalRow['ReturnToProvision'] = returnToProvisionTotal;
        sectionTotalRow['DeferredTrueUp'] = deferredTrueUpTotal;
        sectionTotalRow['CurrentActivity'] = currentActivityTotal;
      }

      sectionTotalRow['ReturnToProvisionRollforward'] = returnToProvisionRollforwardTotal;
      sectionTotalRow['DeferredTrueUpRollforward'] = deferredTrueUpRollforwardTotal;
      sectionTotalRow['WriteOff'] = writeOffTotal;
      sectionTotalRow['ValuationAllowance'] = valuationAllowanceTotal;

      // For VALUATION ALLOWANCE section totals, these rollforward columns are hidden
      if (adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
        sectionTotalRow['TaxRateChange'] = null;
        sectionTotalRow['ReturnToProvisionRollforward'] = null;
        sectionTotalRow['DeferredTrueUpRollforward'] = null;
        sectionTotalRow['CurrentActivity'] = null;
        sectionTotalRow['WriteOff'] = null;
      }

      deferredRows.push(sectionTotalRow);
    });

    // Add grand total row for all categories
    const grandTotalRow = {
      id: 'grand-total-consolidated',
      EntityCode: '',
      AdjustmentCode: '',
      AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
      isSubFooter: true
    };

    // Calculate grand totals for each activity type from all adjustment rows
    ACTIVITY_TYPES.forEach((activityType) => {
      let grandTotal = 0;
      let grandTaxEffectedTotal = 0;
      let currentSectionType = null;

      deferredRows.forEach((row) => {
        // Track current section type
        if (row.isSection) {
          // Extract section type from section header
          if (row.AdjustmentName.includes('TAX EFFECTED TEMPORARY DIFFERENCES')) {
            currentSectionType = 'Tax Effected Temporary';
          } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE') && row.AdjustmentName.includes('TAX EFFECTED')) {
            currentSectionType = 'Tax Effected Valuation Allowance';
          } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE')) {
            currentSectionType = 'Valuation Allowance';
          } else {
            currentSectionType = 'Other';
          }
        }

        if (!row.isSection && !row.isSubFooter) {
          // For TAX EFFECTED sections and VALUATION ALLOWANCE sections, don't include (pretax) columns in grand total
          if (currentSectionType !== 'Tax Effected Temporary' && currentSectionType !== 'Valuation Allowance' && currentSectionType !== 'Tax Effected Valuation Allowance') {
            grandTotal += parseFloat(row[activityType]) || 0;
          }
          grandTaxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
        }
      });

      grandTotalRow[activityType] = grandTotal;
      grandTotalRow[`${activityType}_TaxEffected`] = grandTaxEffectedTotal;
    });

    // Calculate grand totals for other columns
    let grandCtaAdjustmentTotal = 0;
    let grandCtaAdjustmentTaxEffectedTotal = 0;
    let grandEndingBalanceTotal = 0;
    let grandEndingBalanceTaxEffectedTotal = 0;
    let grandDeferredExpenseTotal = 0;
    let grandTaxRateChangeTotal = 0;
    let grandTaxRateChangeTaxEffectedTotal = 0;
    let grandReturnToProvisionTotal = 0;
    let grandDeferredTrueUpTotal = 0;
    let grandReturnToProvisionRollforwardTotal = 0;
    let grandDeferredTrueUpRollforwardTotal = 0;
    let grandCurrentActivityTotal = 0;
    let grandWriteOffTotal = 0;
    let grandValuationAllowanceTotal = 0;

    deferredRows.forEach((row) => {
      if (!row.isSection && !row.isSubFooter) {
        grandCtaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
        grandCtaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
        grandEndingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
        grandEndingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;
        grandDeferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
        grandTaxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
        grandTaxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
        grandReturnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
        grandDeferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
        grandReturnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
        grandDeferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
        grandCurrentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
        grandWriteOffTotal += parseFloat(row['WriteOff']) || 0;
        grandValuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
      }
    });

    grandTotalRow['CTAAdjustment'] = grandCtaAdjustmentTotal;
    grandTotalRow['CTAAdjustment_TaxEffected'] = grandCtaAdjustmentTaxEffectedTotal;
    grandTotalRow['EndingBalance'] = grandEndingBalanceTotal;
    grandTotalRow['EndingBalance_TaxEffected'] = grandEndingBalanceTaxEffectedTotal;
    grandTotalRow['DeferredExpense'] = grandDeferredExpenseTotal;
    grandTotalRow['TaxRateChange'] = grandTaxRateChangeTotal;
    grandTotalRow['TaxRateChange_TaxEffected'] = grandTaxRateChangeTaxEffectedTotal;
    grandTotalRow['ReturnToProvision'] = grandReturnToProvisionTotal;
    grandTotalRow['DeferredTrueUp'] = grandDeferredTrueUpTotal;
    grandTotalRow['ReturnToProvisionRollforward'] = grandReturnToProvisionRollforwardTotal;
    grandTotalRow['DeferredTrueUpRollforward'] = grandDeferredTrueUpRollforwardTotal;
    grandTotalRow['CurrentActivity'] = grandCurrentActivityTotal;
    grandTotalRow['WriteOff'] = grandWriteOffTotal;
    grandTotalRow['ValuationAllowance'] = grandValuationAllowanceTotal;

    deferredRows.push(grandTotalRow);

    return { deferredRows, activityTypes: ACTIVITY_TYPES };
  };

  // Pivot data to use entities as columns and adjustments as rows, grouped by sections
  const pivotData = (data) => {








    // Always use scenario entityList for consistent alphabetical column ordering
    // This prevents columns from reordering when data changes
    let uniqueEntities;
    if (selectedScenario?.entityList && selectedScenario.entityList.length > 0) {
      // Use scenario's entity list, sorted alphabetically
      uniqueEntities = [...selectedScenario.entityList].sort();

    } else {
      // Fallback: use entities that have data, sorted alphabetically
      uniqueEntities = [...new Set(data.map((row) => row.EntityCode))].
      filter((code) => code).
      sort();

    }

    // Group adjustments by their adjustment type (which maps to sections)
    const groupedByType = {};
    data.forEach((row) => {
      const adjustmentType = row.AdjustmentSource || row.AdjustmentType || 'Other';
      if (!groupedByType[adjustmentType]) {
        groupedByType[adjustmentType] = [];
      }
      groupedByType[adjustmentType].push(row);
    });

    const pivotedRows = [];
    let isFirstSection = true;
    let sectionTotalsCache = {}; // Cache to store section totals for calculations

    // Process each section in order - show all sections regardless of data
    // Use currentProvisionSectionMapping to exclude deferred-only sections
    Object.entries(currentProvisionSectionMapping).forEach(([adjustmentType, sectionTitle]) => {
      const sectionData = groupedByType[adjustmentType];

      // Add sub-footer for previous section (except for the first section)
      if (!isFirstSection && pivotedRows.length > 0) {
        // Calculate totals for the previous section
        const previousAdjustmentType = Object.keys(currentProvisionSectionMapping)[Object.keys(currentProvisionSectionMapping).indexOf(adjustmentType) - 1];
        const sectionTotals = {
          id: `footer-${adjustmentType}`,
          AdjustmentCode: '',
          AdjustmentName: sectionTotalTitles[previousAdjustmentType] || 'Section Total',
          isSubFooter: true
        };

        // Calculate totals for each entity column
        const currentSectionTotals = {};
        uniqueEntities.forEach((entityCode) => {
          let total = 0;
          // Find all non-header, non-footer rows since the last section header
          for (let i = pivotedRows.length - 1; i >= 0; i--) {
            const row = pivotedRows[i];
            if (row.isSection) break; // Stop at the previous section header
            if (!row.isSection && !row.isSubFooter) {
              total += parseFloat(row[entityCode]) || 0;
            }
          }
          sectionTotals[entityCode] = total;
          currentSectionTotals[entityCode] = total;
        });

        // Cache the section totals for later calculations
        sectionTotalsCache[previousAdjustmentType] = currentSectionTotals;

        pivotedRows.push(sectionTotals);

        // Add special calculated total after GS Temporary section
        if (previousAdjustmentType === 'GS Temporary') {
          const calculatedTotal = {
            id: 'calculated-gaap-book-income',
            AdjustmentCode: '',
            AdjustmentName: 'STATUTORY PRETAX BOOK INCOME',
            isCalculatedTotal: true
          };

          // Calculate: Pretax total - GS Permanent total + GS Temporary total
          uniqueEntities.forEach((entityCode) => {
            const pretaxTotal = sectionTotalsCache['Pretax']?.[entityCode] || 0;
            const gsPermTotal = sectionTotalsCache['GS Permanent']?.[entityCode] || 0;
            const gsTempTotal = currentSectionTotals[entityCode] || 0;

            calculatedTotal[entityCode] = pretaxTotal - gsPermTotal + gsTempTotal;
          });

          // Cache the statutory pretax book income for later calculations
          sectionTotalsCache['Statutory Pretax'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Statutory Pretax'][entityCode] = calculatedTotal[entityCode];
          });

          pivotedRows.push(calculatedTotal);
        }

        // Add special calculated total after Temporary section (STAT TO TAX TEMPORARY DIFFERENCES)
        if (previousAdjustmentType === 'Temporary') {
          const taxableIncomeTotal = {
            id: 'calculated-taxable-income-before-adjustments',
            AdjustmentCode: '',
            AdjustmentName: 'TAXABLE INCOME BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          // Calculate: Statutory Pretax Book Income - Permanent - Payable - Temporary totals
          uniqueEntities.forEach((entityCode) => {
            const statutoryPretaxTotal = sectionTotalsCache['Statutory Pretax']?.[entityCode] || 0;
            const permanentTotal = sectionTotalsCache['Permanent']?.[entityCode] || 0;
            const payableTotal = sectionTotalsCache['Payable']?.[entityCode] || 0;
            const temporaryTotal = currentSectionTotals[entityCode] || 0;

            taxableIncomeTotal[entityCode] = statutoryPretaxTotal - permanentTotal - payableTotal - temporaryTotal;
          });

          // Cache the taxable income before adjustments for later calculations
          sectionTotalsCache['Taxable Income Before Adjustments'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Taxable Income Before Adjustments'][entityCode] = taxableIncomeTotal[entityCode];
          });

          pivotedRows.push(taxableIncomeTotal);
        }

        // Add special calculated total after TI temporary section (TAXABLE INCOME TEMP ADJUSTMENT)
        if (previousAdjustmentType === 'TI temporary') {
          const taxableIncomeBeforeNOL = {
            id: 'calculated-taxable-income-before-nol',
            AdjustmentCode: '',
            AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
            isCalculatedTotal: true
          };

          // Calculate: Taxable Income Before Adjustments + TI Permanent + TI Temporary totals
          uniqueEntities.forEach((entityCode) => {
            const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[entityCode] || 0;
            const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0;
            const tiTempTotal = currentSectionTotals[entityCode] || 0;

            // Debug logging






            taxableIncomeBeforeNOL[entityCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
          });

          // Cache the taxable income before NOL for later calculations
          sectionTotalsCache['Taxable Income Before NOL'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Taxable Income Before NOL'][entityCode] = taxableIncomeBeforeNOL[entityCode];
          });

          pivotedRows.push(taxableIncomeBeforeNOL);
        }

        // Add TAXABLE INCOME BEFORE NOL calculation right before Net Operating Loss section
        // This ensures it runs even if TI temporary section doesn't exist
        if (adjustmentType === 'Net Operating Loss') {
          // Only add if we haven't already added it after TI temporary section
          const alreadyAdded = pivotedRows.some((row) => row.id === 'calculated-taxable-income-before-nol');
          if (!alreadyAdded) {
            const taxableIncomeBeforeNOL = {
              id: 'calculated-taxable-income-before-nol',
              AdjustmentCode: '',
              AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
              isCalculatedTotal: true
            };

            // Calculate: Taxable Income Before Adjustments + TI Permanent + TI Temporary totals
            uniqueEntities.forEach((entityCode) => {
              const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[entityCode] || 0;
              const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0;
              const tiTempTotal = sectionTotalsCache['TI Temporary']?.[entityCode] || 0;

              // Debug logging






              taxableIncomeBeforeNOL[entityCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
            });

            // Cache the taxable income before NOL for later calculations
            sectionTotalsCache['Taxable Income Before NOL'] = {};
            uniqueEntities.forEach((entityCode) => {
              sectionTotalsCache['Taxable Income Before NOL'][entityCode] = taxableIncomeBeforeNOL[entityCode];
            });

            pivotedRows.push(taxableIncomeBeforeNOL);
          }
        }

        // Add special calculated total after Net Operating Loss section
        if (previousAdjustmentType === 'Net Operating Loss') {
          const totalTaxableIncome = {
            id: 'calculated-total-taxable-income',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL TAXABLE INCOME',
            isCalculatedTotal: true
          };

          // Calculate: TOTAL NET OPERATING LOSS + Taxable Income Before NOL
          uniqueEntities.forEach((entityCode) => {
            // Use cached "Taxable Income Before NOL" value from earlier calculation
            const taxableIncomeBeforeNOL = sectionTotalsCache['Taxable Income Before NOL']?.[entityCode] || 0;
            const netOperatingLossTotal = currentSectionTotals[entityCode] || 0;

            // Debug logging





            totalTaxableIncome[entityCode] = netOperatingLossTotal + taxableIncomeBeforeNOL;
          });

          // Cache the total taxable income for later calculations
          sectionTotalsCache['Total Taxable Income'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Total Taxable Income'][entityCode] = totalTaxableIncome[entityCode];
          });

          pivotedRows.push(totalTaxableIncome);

          // Add current tax rate row immediately after total taxable income
          const currentTaxRate = {
            id: 'calculated-current-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX RATE PERCENTAGE',
            isCalculatedTotal: true
          };

          // Get current tax rate from EntityInput for each entity
          uniqueEntities.forEach((entityCode) => {
            const entityInput = entityInputs.find((ei) => ei.EntityCode === entityCode);
            const taxRate = entityInput?.CurrentTaxRate || 0;

            // Display as percentage (e.g., 10.00%)
            const taxRatePercentage = taxRate > 1 ? taxRate : taxRate * 100;
            currentTaxRate[entityCode] = taxRatePercentage;
          });

          // Cache the current tax rates as decimals for calculations
          sectionTotalsCache['Current Tax Rate'] = {};
          uniqueEntities.forEach((entityCode) => {
            const entityInput = entityInputs.find((ei) => ei.EntityCode === entityCode);
            const taxRate = entityInput?.CurrentTaxRate || 0;
            const taxRateDecimal = taxRate > 1 ? taxRate / 100 : taxRate;
            sectionTotalsCache['Current Tax Rate'][entityCode] = taxRateDecimal;
          });

          pivotedRows.push(currentTaxRate);

          // Add current tax expense before adjustments row
          const currentTaxExpense = {
            id: 'calculated-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX EXPENSE BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          // Calculate: Total Taxable Income × Current Tax Rate
          uniqueEntities.forEach((entityCode) => {
            const totalTaxableIncomeValue = sectionTotalsCache['Total Taxable Income']?.[entityCode] || 0;
            const currentTaxRateValue = sectionTotalsCache['Current Tax Rate']?.[entityCode] || 0;

            currentTaxExpense[entityCode] = totalTaxableIncomeValue * currentTaxRateValue;
          });

          // Cache the current tax expense for later calculations
          sectionTotalsCache['Current Tax Expense Before Adjustments'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Current Tax Expense Before Adjustments'][entityCode] = currentTaxExpense[entityCode];
          });

          pivotedRows.push(currentTaxExpense);
        }

        // Add special calculated total after Tax Adj. Return section (RETURN BASIS TAX ADJUSTMENTS)
        if (previousAdjustmentType === 'Tax Adj. Return') {
          const totalReturnBasisTaxExpense = {
            id: 'calculated-total-return-basis-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL RETURN BASIS CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: Current Tax Expense Before Adjustments - Tax Effected Temporary - Return Basis Tax Adjustments
          uniqueEntities.forEach((entityCode) => {
            const currentTaxExpenseValue = sectionTotalsCache['Current Tax Expense Before Adjustments']?.[entityCode] || 0;
            const taxEffectedTempTotal = sectionTotalsCache['Tax Effected Temporary']?.[entityCode] || 0;
            const returnBasisTaxAdjTotal = currentSectionTotals[entityCode] || 0;

            totalReturnBasisTaxExpense[entityCode] = currentTaxExpenseValue - taxEffectedTempTotal - returnBasisTaxAdjTotal;
          });

          // Cache the total return basis current tax expense for later calculations
          sectionTotalsCache['Total Return Basis Current Tax Expense'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Total Return Basis Current Tax Expense'][entityCode] = totalReturnBasisTaxExpense[entityCode];
          });

          pivotedRows.push(totalReturnBasisTaxExpense);
        }

      }

      // Always add section header row
      const sectionHeaderRow = {
        id: `section-${adjustmentType}`,
        AdjustmentCode: '',
        AdjustmentName: sectionTitle,
        isSection: true
      };
      uniqueEntities.forEach((entityCode) => {
        sectionHeaderRow[entityCode] = null;
      });
      pivotedRows.push(sectionHeaderRow);

      // Add adjustment rows for this section if data exists
      if (sectionData && sectionData.length > 0) {
        // Group adjustments by adjustment code to track multiple entries
        const adjustmentCodeGroups = {};

        sectionData.forEach((row) => {
          const code = row.AdjustmentCode;
          if (!adjustmentCodeGroups[code]) {
            adjustmentCodeGroups[code] = [];
          }
          adjustmentCodeGroups[code].push(row);
        });

        // Process each adjustment code group - combine all rows with same code
        Object.keys(adjustmentCodeGroups).sort().forEach((adjustmentCode) => {
          const adjustmentsWithThisCode = adjustmentCodeGroups[adjustmentCode];
          const adjustmentName = adjustmentsWithThisCode[0]?.AdjustmentName || '';

          // Create a single combined row for this adjustment code
          const pivotedRow = {
            id: `${adjustmentCode}_combined`,
            AdjustmentCode: adjustmentCode,
            AdjustmentName: adjustmentName,
            isSection: false
          };

          // Add entity data as columns - sum all amounts for each entity
          uniqueEntities.forEach((entityCode) => {
            // Find all adjustments for this entity and adjustment code
            const entityAdjustments = adjustmentsWithThisCode.filter((row) =>
            row.EntityCode === entityCode
            );

            // Sum all amounts for this entity
            let totalAmount = 0;
            entityAdjustments.forEach((adj) => {
              const amount = selectedCurrency === 'USD' ?
              adj.USDInput || 0 :
              adj.LocalCurrencyInput || 0;
              totalAmount += amount;
            });

            pivotedRow[entityCode] = totalAmount;
          });

          pivotedRows.push(pivotedRow);
        });
      }

      isFirstSection = false;
    });

    // Add final sub-footer for the last section
    if (pivotedRows.length > 0) {
      const lastAdjustmentType = Object.keys(currentProvisionSectionMapping)[Object.keys(currentProvisionSectionMapping).length - 1];
      const finalSectionTotals = {
        id: `footer-final`,
        AdjustmentCode: '',
        AdjustmentName: sectionTotalTitles[lastAdjustmentType] || 'Section Total',
        isSubFooter: true
      };

      // Calculate totals for each entity column for the last section
      const currentSectionTotals = {};
      uniqueEntities.forEach((entityCode) => {
        let total = 0;
        // Find all non-header, non-footer rows since the last section header
        for (let i = pivotedRows.length - 1; i >= 0; i--) {
          const row = pivotedRows[i];
          if (row.isSection) break; // Stop at the last section header
          if (!row.isSection && !row.isSubFooter) {
            total += parseFloat(row[entityCode]) || 0;
          }
        }
        finalSectionTotals[entityCode] = total;
        currentSectionTotals[entityCode] = total;
      });

      // Cache the final section totals
      sectionTotalsCache[lastAdjustmentType] = currentSectionTotals;

      pivotedRows.push(finalSectionTotals);

      // Add the final calculated total after the last section (Tax Adj. RTP)
      if (lastAdjustmentType === 'Tax Adj. RTP') {
        const totalCurrentTaxExpense = {
          id: 'calculated-total-current-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL CURRENT TAX EXPENSE',
          isCalculatedTotal: true
        };

        // Calculate: Total Return Basis Current Tax Expense + Provision Basis Tax Adjustments + Return-to-Provision Adjustments
        uniqueEntities.forEach((entityCode) => {
          const returnBasisTaxExpenseValue = sectionTotalsCache['Total Return Basis Current Tax Expense']?.[entityCode] || 0;
          const provisionBasisTaxAdjTotal = sectionTotalsCache['Tax Adj. Provision']?.[entityCode] || 0;
          const returnToProvisionAdjTotal = currentSectionTotals[entityCode] || 0;

          totalCurrentTaxExpense[entityCode] = returnBasisTaxExpenseValue + provisionBasisTaxAdjTotal + returnToProvisionAdjTotal;
        });

        pivotedRows.push(totalCurrentTaxExpense);

        // Add total deferred tax expense row
        const totalDeferredTaxExpense = {
          id: 'calculated-total-deferred-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL DEFERRED TAX EXPENSE',
          isCalculatedTotal: true
        };

        // Calculate total deferred tax expense from deferred rollforward data
        // Get the deferred rollforward data to extract DeferredExpense grand totals
        const { deferredRows } = createDeferredRollforwardData(data);

        uniqueEntities.forEach((entityCode) => {
          // Find the grand total row for this entity in the deferred rollforward data
          const grandTotalRow = deferredRows.find((row) =>
          row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
          row.EntityCode === '' && // Grand total rows have empty EntityCode
          row.id === `grand-total-${entityCode}`
          );

          // Get the DeferredExpense value from the grand total row
          const deferredExpenseTotal = grandTotalRow?.DeferredExpense || 0;

          totalDeferredTaxExpense[entityCode] = deferredExpenseTotal;
        });

        pivotedRows.push(totalDeferredTaxExpense);

        // Add total tax expense row
        const totalTaxExpense = {
          id: 'calculated-total-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL TAX EXPENSE',
          isCalculatedTotal: true
        };

        // Calculate: Total Current Tax Expense + Total Deferred Tax Expense
        uniqueEntities.forEach((entityCode) => {
          const currentTaxExpenseValue = totalCurrentTaxExpense[entityCode] || 0;
          const deferredTaxExpenseValue = totalDeferredTaxExpense[entityCode] || 0;

          totalTaxExpense[entityCode] = currentTaxExpenseValue + deferredTaxExpenseValue;
        });

        pivotedRows.push(totalTaxExpense);
      }
    }

    return { pivotedRows, uniqueEntities };
  };

  // Filter and transform data using useMemo to prevent flickering
  const displayRows = useMemo(() => {

    let filteredRows = allRows;

    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filteredRows = filteredRows.filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);
    }

    const entityArrRows = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntitiesRows = entityArrRows.filter((e) => !e.startsWith('[Group] '));
    if (plainEntitiesRows.length > 0) {
      filteredRows = filteredRows.filter((row) => plainEntitiesRows.includes(row.EntityCode));
    }

    if (selectedTag && selectedTag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.Tag === selectedTag);
    }

    if (selectedSubtag && selectedSubtag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.SubTag === selectedSubtag);
    }



    // Handle different calculation types
    let finalRows = [];

    if (calculationType === 'deferred-rollforward') {
      // Create deferred rollforward table structure based on view mode
      const { deferredRows } = deferredViewMode === 'consolidated' ?
      createConsolidatedDeferredRollforwardData(filteredRows) :
      createDeferredRollforwardData(filteredRows);
      finalRows = deferredRows;
    } else {
      // Use filteredRows instead of allRows for pivot to ensure updates are reflected
      const { pivotedRows } = pivotData(filteredRows);

      // Filter rows based on calculation type
      finalRows = pivotedRows;
      if (calculationType === 'effective-tax-rate') {
        // Show only total rows (sub-footers and calculated totals, hide section headers)
        finalRows = pivotedRows.filter((row) =>
        row.isSubFooter || row.isCalculatedTotal
        );
      }
    }

    // Filter out rows that belong to collapsed sections
    if (collapsedSections.size > 0) {
      const filteredFinalRows = [];
      let currentSectionId = null;
      let isCurrentSectionCollapsed = false;

      for (let i = 0; i < finalRows.length; i++) {
        const row = finalRows[i];

        // Track current section
        if (row.isSection) {
          currentSectionId = row.id;
          isCurrentSectionCollapsed = collapsedSections.has(currentSectionId);
          // Always show section headers
          filteredFinalRows.push(row);
        } else if (row.isSubFooter || row.isCalculatedTotal) {
          // Always show subtotals and calculated totals
          filteredFinalRows.push(row);
          // Reset section tracking after subtotal/calculated total
          if (row.isSubFooter) {
            currentSectionId = null;
            isCurrentSectionCollapsed = false;
          }
        } else {
          // Regular adjustment rows - only show if section is not collapsed
          if (!isCurrentSectionCollapsed) {
            filteredFinalRows.push(row);
          }
        }
      }

      finalRows = filteredFinalRows;
    }


    return finalRows;
  }, [allRows, selectedScenario, selectedEntity, selectedTag, selectedSubtag, selectedCurrency, calculationType, deferredViewMode, collapsedSections]);

  // Reports page is read-only - no edit handlers needed

  // Dynamic columns with entities as headers
  const columns = useMemo(() => {
    if (calculationType === 'deferred-rollforward') {
      // Create columns for deferred rollforward view
      const deferredColumns = [
      {
        field: 'AdjustmentName',
        headerName: 'Adjustment Name',
        width: 600,
        maxWidth: 400,
        renderCell: (params) => {
          if (params.row.isSection) {
            const isCollapsed = collapsedSections.has(params.row.id);
            return (
              <Box
                onClick={() => handleSectionClick(params.row.id)}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#424242',
                  color: 'white',
                  padding: '8px',
                  width: '100%',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    backgroundColor: '#616161'
                  }
                }}>

                  <span>{params.value}</span>
                  <ExpandMoreIcon
                  sx={{
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }} />

                </Box>);

          }
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '8px',
                width: '100%',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right'
              }}>
                  {params.value}
                </Box>);

          }
          // For regular adjustment rows, show EntityCode + AdjustmentCode + AdjustmentName
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
                <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                  {params.row.EntityCode}
                </Box>
                <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                  {params.row.AdjustmentCode}
                </Box>
                <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                  {params.value}
                </Box>
              </Box>);

        }
      }];


      // Add regular activity type columns (first set)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'DeferredTrueUp') {
          headerName = 'DEFERRED TRUE UP (PRETAX)';
        } else if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (PRETAX)';
        } else if (activityType === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (PRETAX)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (PRETAX)`;
        }

        deferredColumns.push({
          field: activityType,
          headerName: headerName,
          minWidth: 120,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Regular values - no editing in Reports
            return params.formattedValue;
          }
        });
      });

      // Add CTA Adjustment column - only if showing gross or both
      if (columnTypeFilter === 'gross' || columnTypeFilter === 'both') {
        deferredColumns.push({
          field: 'CTAAdjustment',
          headerName: 'CTA ADJUSTMENT (PRETAX)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Regular values - no editing in Reports
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance column - only if showing gross or both
      if (columnTypeFilter === 'gross' || columnTypeFilter === 'both') {
        deferredColumns.push({
          field: 'EndingBalance',
          headerName: 'ENDING BALANCE (PRETAX)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Regular values - no editing in Reports
            return params.formattedValue;
          }
        });
      }

      // Add tax effected activity type columns (second set) - only if showing tax-effected or both
      if (columnTypeFilter === 'tax-effected' || columnTypeFilter === 'both') {
        ACTIVITY_TYPES.forEach((activityType) => {
          let headerName;
          if (activityType === 'AdjBeginningBalance') {
            headerName = 'ADJ. BEGINNING BALANCE (TAX EFFECTED)';
          } else {
            headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (TAX EFFECTED)`;
          }

          deferredColumns.push({
            field: `${activityType}_TaxEffected`,
            headerName: headerName,
            minWidth: 140,
            type: 'number',
            valueFormatter: (params) => {
              if (params.value === null || params.value === undefined || params.value === 0) return '-';
              return formatRoundedNumberWithParentheses(params.value);
            },
            renderCell: (params) => {
              // Don't show values for section headers
              if (params.row.isSection) {
                return null;
              }
              // Style sub-footer totals
              if (params.row.isSubFooter) {
                return (
                  <Box sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    borderTop: '1px solid #ccc',
                    textAlign: 'right',
                    width: '100%'
                  }}>
                  {params.formattedValue}
                </Box>);

              }
              // Style calculated totals
              if (params.row.isCalculatedTotal) {
                return (
                  <Box sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    borderTop: '1px solid #ccc',
                    textAlign: 'right',
                    width: '100%'
                  }}>
                  {params.formattedValue}
                </Box>);

              }
              // Regular values
              return params.formattedValue;
            }
          });
        });
      }

      // Add Tax Rate Change Tax Effected column - only if showing tax-effected or both
      if (columnTypeFilter === 'tax-effected' || columnTypeFilter === 'both') {
        deferredColumns.push({
          field: 'TaxRateChange_TaxEffected',
          headerName: 'TAX RATE CHANGE (TAX EFFECTED)',
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      }

      // Add CTA Adjustment Tax Effected column
      if (columnTypeFilter === 'tax-effected' || columnTypeFilter === 'both') {
        deferredColumns.push({
          field: 'CTAAdjustment_TaxEffected',
          headerName: 'CTA ADJUSTMENT (TAX EFFECTED)',
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance Tax Effected column
      if (columnTypeFilter === 'tax-effected' || columnTypeFilter === 'both') {
        deferredColumns.push({
          field: 'EndingBalance_TaxEffected',
          headerName: 'ENDING BALANCE (TAX EFFECTED)',
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                {params.formattedValue}
              </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      }

      // Add new calculated columns - conditionally based on showDeferredETR
      const newColumns = showDeferredETR ? [
      'DeferredExpense',
      'TaxRateChange',
      'ReturnToProvision',
      'DeferredTrueUpFinal',
      'CurrentActivity',
      'WriteOff',
      'ValuationAllowance'] :
      [];

      newColumns.forEach((columnName) => {
        // Determine the header name - rollforward columns don't have (TAX EFFECTED) or (PRETAX) labels
        let headerName;
        let fieldName;
        if (columnName === 'DeferredTrueUpFinal') {
          headerName = 'DEFERRED TRUE UP';
          fieldName = 'DeferredTrueUpRollforward';
        } else if (columnName === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION';
          fieldName = 'ReturnToProvisionRollforward';
        } else {
          headerName = columnName.replace(/([A-Z])/g, ' $1').trim().toUpperCase().replace(/TO/g, 'TO').replace(/UP/g, 'UP').replace(/OFF/g, 'OFF');
          fieldName = columnName;
        }

        deferredColumns.push({
          field: fieldName,
          headerName: headerName,
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Regular values - no editing in Reports
            return params.formattedValue;
          }
        });
      });

      return deferredColumns;
    }

    if (allRows.length === 0) {
      return [
      { field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
      { field: 'AdjustmentName', headerName: 'Adjustment Name', width: 600 }];

    }

    // Get unique entities from filtered data based on selected scenario, entity, and tag
    let filteredRows = allRows;
    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filteredRows = filteredRows.filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);
    }

    const entityArrCols = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntitiesCols = entityArrCols.filter((e) => !e.startsWith('[Group] '));
    const groupSelectionsCols = entityArrCols.filter((e) => e.startsWith('[Group] '));
    // Filter rows by plain entity selections (groups handled via column filtering)
    if (plainEntitiesCols.length > 0) {
      filteredRows = filteredRows.filter((row) => plainEntitiesCols.includes(row.EntityCode));
    }

    if (selectedTag && selectedTag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.Tag === selectedTag);
    }
    if (selectedSubtag && selectedSubtag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.SubTag === selectedSubtag);
    }

    // Get all unique entities from the data
    let uniqueEntities = [...new Set(filteredRows.map((row) => row.EntityCode))].
    filter((code) => code).
    sort();

    // Apply entity filter to columns
    if (entityArrCols.length > 0) {
      let allowedCodes = [...plainEntitiesCols];
      if (groupSelectionsCols.length > 0) {
        groupSelectionsCols.forEach((sel) => {
          const groupName = sel.replace('[Group] ', '');
          const selectedGroup = entityGroups.find((g) => g.name === groupName);
          if (selectedGroup?.entityList?.length > 0) {
            const groupEntityCodes = selectedGroup.entityList.
            map((entityId) => masterEntities.find((e) => e.id === entityId)?.EntityCode).
            filter((code) => code);
            allowedCodes = [...allowedCodes, ...groupEntityCodes];
          }
        });
      }
      if (allowedCodes.length > 0) {
        uniqueEntities = uniqueEntities.filter((code) => allowedCodes.includes(code));
      }
    }

    // Create base columns for adjustment info
    const baseColumns = [
    {
      field: 'AdjustmentName',
      headerName: 'Adjustment Name',
      width: 600,
      maxWidth: 400,
      renderCell: (params) => {
        if (params.row.isSection) {
          const isCollapsed = collapsedSections.has(params.row.id);
          return (
            <Box
              onClick={() => handleSectionClick(params.row.id)}
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#424242',
                color: 'white',
                padding: '8px',
                width: '100%',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': {
                  backgroundColor: '#616161'
                }
              }}>

              <span>{params.value}</span>
              <ExpandMoreIcon
                sx={{
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }} />

            </Box>);

        }
        if (params.row.isSubFooter) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              width: '100%',
              borderRadius: '4px',
              borderTop: '1px solid #ccc',
              textAlign: 'right'
            }}>
              {params.value}
            </Box>);

        }
        if (params.row.isCalculatedTotal) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              width: '100%',
              borderRadius: '4px',
              borderTop: '1px solid #ccc',
              textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right'
            }}>
              {params.value}
            </Box>);

        }

        // For regular adjustment rows in current provision mode, show code + name with custom layout
        if (calculationType === 'current-provision' && params.row.AdjustmentCode) {
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
              <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                {params.row.AdjustmentCode}
              </Box>
              <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                {params.value}
              </Box>
            </Box>);

        }

        return params.value;
      }
    }];

    // Add Total Consolidated column before entity columns (only if there are multiple entities)
    if (uniqueEntities.length > 1) {
      baseColumns.push({
        field: 'TotalConsolidated',
        headerName: 'Total Consolidated',
        minWidth: 180,
        type: 'number',
        valueGetter: (params) => {
          // Don't calculate for section headers
          if (params.row.isSection) {
            return null;
          }

          // Calculate total of all entity values for this row
          let total = 0;
          uniqueEntities.forEach((entityCode) => {
            const entityValue = params.row[entityCode];
            if (entityValue && typeof entityValue === 'number') {
              total += entityValue;
            }
          });

          return total === 0 ? null : total;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';

          // Special formatting for Current Tax Rate row - show as percentage with % symbol
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            return `${params.value.toFixed(2)}%`;
          }

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Make section headers clickable across entire row
          if (params.row.isSection) {
            return (
              <Box
                onClick={() => handleSectionClick(params.row.id)}
                sx={{
                  width: '100%',
                  height: '100%'
                }} />);


          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
              {params.formattedValue}
            </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
              {params.formattedValue}
            </Box>);

          }
          // Regular cells - not clickable for Total Consolidated column
          return (
            <Box sx={{
              padding: '4px 8px',
              borderRadius: '4px',
              textAlign: 'right',
              width: '100%'
            }}>
            {params.formattedValue}
          </Box>);

        }
      });
    }

    // Add entity columns
    uniqueEntities.forEach((entityCode) => {
      const entity = masterEntities.find((e) => e.EntityCode === entityCode);
      const entityName = entity?.EntityName || entityCode;

      baseColumns.push({
        field: entityCode,
        headerName: `${entityName} (${entityCode})`,
        minWidth: 200,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';

          // Special formatting for Current Tax Rate row - show as percentage with % symbol
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            return `${params.value.toFixed(2)}%`;
          }

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Make section headers clickable across entire row
          if (params.row.isSection) {
            return (
              <Box
                onClick={() => handleSectionClick(params.row.id)}
                sx={{
                  width: '100%',
                  height: '100%'
                }} />);


          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Regular values - no editing in Reports
          return params.formattedValue;
        }
      });
    });



    return baseColumns;
  }, [allRows, masterEntities, entityGroups, selectedCurrency, calculationType, selectedScenario, selectedEntity, selectedTag, deferredViewMode, columnTypeFilter, showDeferredETR, collapsedSections]);

  return (
    <div className="entity-inputs-container">
      <div className="main-content">
        <div className="toolbar-container">
          <Typography className="page-title" variant="h6">Reports</Typography>
          <div className="button-group">
            {calculationType === 'deferred-rollforward' &&
            <>
                <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '4px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                marginRight: 2
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', mr: 1, whiteSpace: 'nowrap' }}>
                    Breakouts
                  </Typography>
                  <RadioGroup
                  row
                  value={deferredViewMode}
                  onChange={(e) => setDeferredViewMode(e.target.value)}>

                    <FormControlLabel value="unit-breakout" control={<Radio size="small" />} label="Unit" />
                    <FormControlLabel value="consolidated" control={<Radio size="small" />} label="Consolidated" />
                  </RadioGroup>
                </Box>
                <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '4px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                marginRight: 2
              }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', mr: 1, whiteSpace: 'nowrap' }}>
                    Sections
                  </Typography>
                  <RadioGroup
                  row
                  value={columnTypeFilter}
                  onChange={(e) => setColumnTypeFilter(e.target.value)}>

                    <FormControlLabel value="gross" control={<Radio size="small" />} label="Gross" />
                    <FormControlLabel value="tax-effected" control={<Radio size="small" />} label="Tax Effected" />
                    <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
                  </RadioGroup>
                  {columnTypeFilter !== 'gross' &&
                <FormControlLabel
                  control={
                  <Checkbox
                    checked={showDeferredETR}
                    onChange={(e) => setShowDeferredETR(e.target.checked)}
                    size="small" />

                  }
                  label={<Typography variant="caption">Deferred Effective Tax Rate</Typography>}
                  sx={{ ml: 1 }} />

                }
                </Box>
              </>
            }
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}
              disabled={rows.length === 0}
              sx={{
                '&.Mui-disabled': {
                  border: '2px solid rgba(0,0,0,0.2) !important',
                  color: 'rgba(0,0,0,0.3) !important',
                  backgroundColor: 'rgba(0,0,0,0.05) !important',
                  pointerEvents: 'auto',
                  cursor: 'not-allowed',
                  '& .MuiButton-startIcon': {
                    color: 'rgba(0,0,0,0.3) !important'
                  }
                }
              }}>

              Export
            </Button>
          </div>
        </div>
        <div className="table-container">
          {loading && <LinearProgress />}
          <EnhancedDataTable
            rows={displayRows}
            columns={columns}
            loading={loading}
            tableName="reports"
            height="100%"
            className="custom-datagrid"
            disableColumnReorder={true}
            disableColumnSorting={true}
            pinnedColumns={{ right: ['actions'], left: ['AdjustmentName'] }}
            getRowClassName={(params) => {
              return params.row.isSection ? 'section-header' : '';
            }} />

        </div>
      </div>
      
      {/* Snackbar for notifications */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>);

};

export default TaxCalculations;
